/* eslint-disable no-loop-func */
import { EventTypeData } from '../../types/ViewModel/EventTypeData';
import { EventTypeGroup } from '../../types/ViewModel/EventTypeGroup';
import { EventType } from '../../types/ViewModel/EventType';
import { TankersEvent } from '../../types/ViewModel/TankersEvent';
import { resolveEventTypeName, resolveEventTypeNameForNewEvent } from './eventTypeNameOverrides';
import { ConditionalMandatory } from '../../types/ViewModel/ConditionalMandatory';
import { PortCall } from '../../types/ViewModel/PortCall';
import { BerthCall } from '../../types/ViewModel/BerthCall';
import EventTypeFactory from '../../factory/EventTypeFactory';
import { DropdownItemFactory } from '../../factory/DropdownItemFactory';
import { IRotationNode } from '../../types/ViewModel/IRotationNode';
import { CargoOperation } from '../../types/ViewModel/CargoOperation';
import { InputDataTypeEnum } from '../../enums/inputDataTypeEnum';
import { RotationTreeEnum } from '../../enums/rotationTreeEnum';
import useEvent from '../../app/useEvent';
import useEventType from '../../app/useEventType';
import useRotation from '../../app/useRotation';
import { MoveActionMsg } from '../../types/ViewModel/ActionMsg';
import { Rotation } from '../../types/ViewModel/Rotation';
import { useEventTypeStore } from '../../context/EventTypeContext';
import { useEventStore } from '../../context/EventContext';
import eventtypecommon from './eventtypecommon';
import { ReportLocationEnum } from '../../enums/reportLocationEnum';
import {
    EOSP,
    SOSP,
    ADJUST_HOURS,
    ADJUST_HOURS_DD,
    ADJUST_HOURS_MINUS_2H,
    ADJUST_HOURS_MINUS_15H,
    ADJUST_HOURS_MINUS_1H,
    ADJUST_HOURS_MINUS_05H,
    ADJUST_HOURS_PLUSS_2H,
    ADJUST_HOURS_PLUSS_15H,
    ADJUST_HOURS_PLUSS_1H,
    ADJUST_HOURS_PLUSS_05H,
    IDL_CROSSING,
    IDL_CROSSING_DD,
    IDL_CROSSING_MINUS_24H,
    IDL_CROSSING_PLUSS_24H,
} from '../../types/ViewModel/TankersEventType';
import { FrontendVersionEnum } from '../../enums/frontendVersionEnum';
import featureUtils from '../featureUtils';
import { Feature } from '../../enums/featureEnum';
import eventTypeGroupChangeLogUtils from './eventTypeGroupChangeLogUtils';
import { isDateAfter } from 'odfjell-date-time-utils/dateTimeUtils';
import { compareActualEventDateTime, getDateWithoutSecMs, getImosOffset } from './eventDateUtils';
import { getPortCallName } from 'src/factory/ReportEvents/Rotation/PortCallFactory';

export default () => {
    const dropdownItemFactory = new DropdownItemFactory();
    const {
        generateUuid,
        isRequiredEventTypeInSameEventTypeGroup,
        sortOrderedEvents,
        filterDuplicateEvents,
        findMissingEventTypeInSequence,
        entityOk,
        getParentEvents,
        hasConcurrentParentInCompleteTree,
        getConcurrentParent,
        generateDependencyDisplayName,
        doesDropdownValueExist,
        sortOrderedEventTypes,
        generateDisplayName,
    } = eventtypecommon();
    const { eventTypeDataPort, eventTypeDataSea } = useEventTypeStore();
    const { fullEventArray } = useEventStore();
    const { getEventType, getEventTypeGroup, filterEventTypeGroup } = useEventType();
    const { getRequiredContextObj, filterCargoOperation, getPortCallFromNode, getBerthCallFromNode, getPortCallIdFromNode, getCargoOpRotationNode } =
        useRotation();
    const { isFeatureEnabled } = featureUtils();
    const {
        getFullEventArray,
        filterEventsOnDeleted,
        filterEventsOnActual,
        filterOnContext,
        filterOnContextSingleLevelIncludeParents,
        filterOnContextIncludeParentWithBerthCall,
        filterSameEventTypeGroupAndLevel,
        filterOnContextSingleLevel,
        filterOnContextSeaIncludePort,
        eventHasDelayAndMissingInput,
        eventHasNoDelayAndExistingInput,
        eventHasDelayAndExistingInput,
        getRotationNodeFromEvent,
        getFirstPortCallTime,
        isCargoDropdown,
    } = useEvent();

    const { createNewEventType, createNewEventTypeForToolbox } = EventTypeFactory();
    const { isMandatory, isMultiple, isConcurrent, getRequiredPreviousTypeId, getNotAfterEventTypeId } = eventTypeGroupChangeLogUtils();

    const getMandatoryOrderedEventTypeGroups = (evTypeGroupArray: EventTypeGroup[], node: IRotationNode): EventTypeGroup[] => {
        const manEvTypeGroupArray: EventTypeGroup[] = [];
        evTypeGroupArray.forEach((etg) => {
            if (isEtgMandatory(etg, node) && etg.ordered && !etg.deleted) {
                // Check for conditionally mandatory, and include if set
                manEvTypeGroupArray.push(etg);
            }
        });
        return manEvTypeGroupArray;
    };

    const getMandatoryNotOrderedEventTypeGroups = (
        eventArray: TankersEvent[],
        evTypeGroupArrayInput: EventTypeGroup[],
        conditionallyMandatoryArray: ConditionalMandatory[],
        node: IRotationNode
    ): EventTypeGroup[] => {
        const evTypeGroupArray: EventTypeGroup[] = [];
        evTypeGroupArrayInput.forEach((etg) => {
            if (isEtgMandatory(etg, node) && !etg.ordered && !etg.deleted) {
                // Check for conditionally mandatory, and include if set
                if (includeConditionallyMandatory(eventArray, etg, conditionallyMandatoryArray)) {
                    evTypeGroupArray.push(etg);
                }
            }
        });
        return evTypeGroupArray;
    };

    const getOptionalEventTypeGroups = (evTypeGroupArrayInput: EventTypeGroup[], node: IRotationNode): EventTypeGroup[] => {
        const evTypeGroupArray: EventTypeGroup[] = [];
        evTypeGroupArrayInput.forEach((etg) => {
            if (!isEtgMandatory(etg, node) && !etg.deleted) {
                evTypeGroupArray.push(etg);
            }
        });
        return evTypeGroupArray;
    };

    const includeConditionallyMandatory = (
        eventArray: TankersEvent[],
        evTypeGroup: EventTypeGroup,
        conditionallyMandatoryArray: ConditionalMandatory[]
    ): boolean => {
        let include = true;

        if (conditionallyMandatoryArray && conditionallyMandatoryArray.length > 0) {
            // Find entry in ConditionalMandatory for current event type group
            const condMandItem = conditionallyMandatoryArray.find((cmItem) => cmItem.eventTypeGroupId === evTypeGroup.eventTypeGroupId);

            if (condMandItem) {
                // Find all related entries in ConditionalMandatory
                const condMandArray = conditionallyMandatoryArray.filter(
                    (cmItem) => cmItem.conditionalMandatoryIdentity === condMandItem.conditionalMandatoryIdentity
                );

                if (condMandArray && condMandArray.length > 0) {
                    // Check if there are registered events on any other conditionally mandatory event type group, if so return include = 'false'
                    const cMandItem = condMandArray.find((cmItem) => {
                        if (evTypeGroup.eventTypeGroupId !== cmItem.eventTypeGroupId) {
                            const evArray = eventArray.filter((ev) => ev.eventType && ev.eventType.eventTypeGroupId === cmItem.eventTypeGroupId);
                            if (evArray && evArray.length > 0) {
                                return true;
                            }
                        }
                        return false;
                    });
                    include = !cMandItem;
                }
            }
        }
        return include;
    };

    // Gets dependent event type groups, i.e. event type groups with notAfterEventTypeId equal to eventType.eventTypeId in parameter
    // Do not include conditional mandatory
    const getDependentEventTypeGroupArray = (eventType: EventType, eventTypeData: EventTypeData): EventTypeGroup[] => {
        const evTypeGroup: EventTypeGroup[] = [];
        if (eventTypeData && eventTypeData.eventTypeLevelArray && eventTypeData.eventTypeLevelArray.length > 0) {
            eventTypeData.eventTypeLevelArray.forEach((levelItem) => {
                levelItem.eventTypeGroupArray.forEach((groupItem) => {
                    if (groupItem.notAfterEventTypeId && groupItem.notAfterEventTypeId === eventType.eventTypeId) {
                        const condMand = eventTypeData.conditionalMandatoryArray.find((cm) => cm.eventTypeGroupId === groupItem.eventTypeGroupId);
                        if (!condMand) {
                            evTypeGroup.push(groupItem);
                        }
                    }
                });
            });
        }
        return evTypeGroup;
    };

    const getParentEventTypeGroup = (eventTypeId: number, eventTypeData: EventTypeData): EventTypeGroup | null => {
        if (eventTypeData && eventTypeData.eventTypeLevelArray && eventTypeData.eventTypeLevelArray.length > 0) {
            const grArray = eventTypeData.eventTypeLevelArray.flatMap((o) => o.eventTypeGroupArray);
            const typeArray = grArray.flatMap((o) => o.eventTypeArray);
            const evType = typeArray.find((type) => type.eventTypeId === eventTypeId);
            if (evType) {
                return evType.eventTypeGroup;
            }
        }
        return null;
    };

    const getRequiredEventTypeGroup = (eventTypeGroup: EventTypeGroup, eventTypeData: EventTypeData, node: IRotationNode): EventTypeGroup | null => {
        const reqPrevTypeId = getRequiredPreviousTypeId(eventTypeGroup, node.firstTimeInPortCall);
        if (reqPrevTypeId && eventTypeData && eventTypeData.eventTypeLevelArray && eventTypeData.eventTypeLevelArray.length > 0) {
            const grArray = eventTypeData.eventTypeLevelArray.flatMap((o) => o.eventTypeGroupArray);
            const typeArray = grArray.flatMap((o) => o.eventTypeArray);
            const evType = typeArray.find((type) => type.eventTypeId === reqPrevTypeId);
            if (evType) {
                return evType.eventTypeGroup;
            }
        }
        return null;
    };

    // Sort ordered event type groups, by starting at the last, which has NextGroupInLevelId = null, and go backwards until the first
    // Returns the array starting with the first element
    const sortOrderedEventTypeGroups = (evTypeGroupArrayInput: EventTypeGroup[]): EventTypeGroup[] => {
        const evTypeGroupArray: EventTypeGroup[] = [];
        let exit = false;
        const arrLen = evTypeGroupArrayInput.length;
        let arrCnt = 0;
        let nextId: number;

        // Find last event type group
        const lastEventTypeGroup = evTypeGroupArrayInput.find((ev) => !ev.nextGroupInLevelId);
        if (lastEventTypeGroup) {
            evTypeGroupArray.push(lastEventTypeGroup);
            nextId = lastEventTypeGroup.eventTypeGroupId;
            while (arrCnt < arrLen - 1 && !exit) {
                const previousEventTypeGroup = evTypeGroupArrayInput.find((ev) => ev.nextGroupInLevelId === nextId);
                if (previousEventTypeGroup) {
                    evTypeGroupArray.unshift(previousEventTypeGroup);
                    nextId = previousEventTypeGroup.eventTypeGroupId;
                } else {
                    exit = true;
                }
                arrCnt++;
            }
        }
        return evTypeGroupArray;
    };

    // Find next available event type groups of mandatory event type groups
    const getNextAvailableEventTypeGroup = (
        evTypeGroupArrayInput: EventTypeGroup[],
        eventArray: TankersEvent[],
        selectedNode: IRotationNode
    ): EventTypeGroup[] => {
        const evTypeGroupListReturn: EventTypeGroup[] = [];
        let numberOfEventTypeGroups = 0;
        if (evTypeGroupArrayInput && evTypeGroupArrayInput.length > 0) {
            evTypeGroupArrayInput.forEach((item) => {
                // Find all events for actual group, level and entity
                const eventListForGroup: TankersEvent[] = eventArray.filter((event) => {
                    return event.eventType && event.eventType.eventTypeGroupId === item.eventTypeGroupId && entityOk(event, selectedNode, false);
                });
                if (eventListForGroup && eventListForGroup.length > 0) {
                    // Add event group if startet and not finished
                    if (findMissingEventTypeInSequence(eventListForGroup) !== -1) {
                        evTypeGroupListReturn.push(item);
                    }
                    numberOfEventTypeGroups++;
                }
            });
            // Add the next event type group in level (if not the last)
            if (numberOfEventTypeGroups < evTypeGroupArrayInput.length) {
                evTypeGroupListReturn.push(evTypeGroupArrayInput[numberOfEventTypeGroups]);
            }
        }
        return evTypeGroupListReturn;
    };

    // Find all available event type groups/event types for list of event groups, check for context and empty/not finished group/multiple
    const getAllAvailableEventTypeGroups = (
        evTypeGroupArrayInput: EventTypeGroup[],
        eventArray: TankersEvent[],
        selectedNode: IRotationNode
    ): EventTypeGroup[] => {
        const evTypeGroupReturn: EventTypeGroup[] = [];

        // Handle null selectedNode gracefully
        if (!selectedNode) {
            return evTypeGroupReturn;
        }

        if (evTypeGroupArrayInput && evTypeGroupArrayInput.length > 0) {
            evTypeGroupArrayInput.forEach((evTypeGroup) => {
                // Find all events for actual group, level and entity
                const eventListForGroup: TankersEvent[] = eventArray.filter((event) => {
                    return (
                        event.eventType && event.eventType.eventTypeGroupId === evTypeGroup.eventTypeGroupId && entityOk(event, selectedNode, false)
                    );
                });
                if (eventListForGroup && eventListForGroup.length > 0) {
                    if (findMissingEventTypeInSequence(eventListForGroup) !== -1) {
                        // Last item not found
                        evTypeGroupReturn.push(evTypeGroup);
                    } else if (isMultiple(evTypeGroup, selectedNode.firstTimeInPortCall)) {
                        evTypeGroupReturn.push(evTypeGroup);
                    }
                } else {
                    evTypeGroupReturn.push(evTypeGroup);
                }
            });
        }
        return evTypeGroupReturn;
    };

    // Check if the event type is dependent on required event type groups to be finished, if one required group is not finished, 'missing' is returned
    const missingRequiredEventTypeGroup = (eventType: EventType, eventArray: TankersEvent[]): boolean => {
        let missing = false;
        let exit = false;
        let ind = 0;
        if (eventType.eventTypeGroupRequiredArray && eventType.eventTypeGroupRequiredArray.length > 0) {
            while (!exit) {
                // Find required event type groups
                const evArr = eventArray.filter((ev) => ev.eventType && eventType.eventTypeGroupRequiredArray[ind] === ev.eventType.eventTypeGroupId);
                if (evArr && evArr.length > 0) {
                    const index = findMissingEventTypeInSequence(evArr);
                    if (index === -1) {
                        missing = false;
                    } else {
                        missing = true;
                        exit = true;
                    }
                }
                if (!exit) {
                    ind++;
                    exit = ind >= eventType.eventTypeGroupRequiredArray.length;
                }
            }
        }
        return missing;
    };

    const hasConditionalMandatory = (evTypeGroup: EventTypeGroup): boolean => {
        return !!(evTypeGroup.requiredConditionalMandatoryArray && evTypeGroup.requiredConditionalMandatoryArray.length > 0);
    };

    // If the event type group has conditional mandatory events check if the required exists and the not after not exist (if specified)
    const isConditionalMandatoryOk = (
        evTypeGroup: EventTypeGroup,
        eventArray: TankersEvent[],
        eventTypeData: EventTypeData,
        isRetroactiveCargoModeEnabled = false,
        node: IRotationNode | null = null
    ): boolean => {
        // 🔓 RETROACTIVE CARGO MODE: Bypass conditional mandatory checks for cargo events
        if (isRetroactiveCargoModeEnabled && node && node.type === RotationTreeEnum.CargoOperation) {
            return true;
        }
        let hasCondMand = false;
        if (evTypeGroup.requiredConditionalMandatoryArray && evTypeGroup.requiredConditionalMandatoryArray.length > 0) {
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                const parentEventTypeGroup = getParentEventTypeGroup(
                    evTypeGroup.requiredConditionalMandatoryArray[ind].requiredPreviousTypeId,
                    eventTypeData
                );
                if (
                    hasStartedNotFinishedParentEventTypeGroup(
                        eventArray,
                        parentEventTypeGroup,
                        evTypeGroup.requiredConditionalMandatoryArray[ind].requiredPreviousTypeId,
                        evTypeGroup.requiredConditionalMandatoryArray[ind].notAfterEventTypeId
                    )
                ) {
                    hasCondMand = true;
                    keepGoing = false;
                } else {
                    ind++;
                    keepGoing = ind < evTypeGroup.requiredConditionalMandatoryArray.length;
                }
            }
        } else {
            hasCondMand = true;
        }
        return hasCondMand;
    };

    // If the event type group has conditional mandatory events check if the required exists and the not after not exist (if specified)
    const isConditionalMandatoryAction = (
        evTypeGroup: EventTypeGroup,
        eventArray: TankersEvent[],
        coFromEventList: TankersEvent[],
        eventTypeData: EventTypeData,
        moveToBerthCall: BerthCall
    ): MoveActionMsg => {
        const actionMsg = new MoveActionMsg(false, null, false, null, null, null);
        let reqEvTypeStr: string | null = null;
        let invalidDateEvTypeStr: string | null = null;
        if (evTypeGroup.requiredConditionalMandatoryArray && evTypeGroup.requiredConditionalMandatoryArray.length > 0) {
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                const parentEventTypeGroup = getParentEventTypeGroup(
                    evTypeGroup.requiredConditionalMandatoryArray[ind].requiredPreviousTypeId,
                    eventTypeData
                );
                if (
                    hasStartedNotFinishedParentEventTypeGroup(
                        eventArray,
                        parentEventTypeGroup,
                        evTypeGroup.requiredConditionalMandatoryArray[ind].requiredPreviousTypeId,
                        null //evTypeGroup.requiredConditionalMandatoryArray[ind].notAfterEventTypeId
                    )
                ) {
                    // Check date/time for dependent event type groups
                    const firstEvInGr = coFromEventList.find((o) => o.eventType?.eventTypeGroupId === evTypeGroup.eventTypeGroupId);
                    if (firstEvInGr) {
                        const actionOk = isAfterStartedNotFinishedParentEventTypeGroup(
                            eventArray,
                            firstEvInGr,
                            parentEventTypeGroup,
                            evTypeGroup.requiredConditionalMandatoryArray[ind].requiredPreviousTypeId
                        );
                        if (!actionOk.doAction) {
                            invalidDateEvTypeStr = actionOk.message;
                            actionMsg.doAction = false;
                        } else {
                            actionMsg.doAction = true;
                        }
                    } else {
                        actionMsg.doAction = true;
                    }
                    keepGoing = false;
                } else {
                    let reqEvType: EventType | null = null;
                    if (eventTypeData) {
                        reqEvType = getEventType(evTypeGroup.requiredConditionalMandatoryArray[ind].requiredPreviousTypeId, null);
                    }
                    if (reqEvType) {
                        if (!reqEvTypeStr) {
                            reqEvTypeStr = "'" + reqEvType.name + "'";
                        } else {
                            reqEvTypeStr = reqEvTypeStr + " or '" + reqEvType.name + "'";
                        }
                    }
                    ind++;
                    keepGoing = ind < evTypeGroup.requiredConditionalMandatoryArray.length;
                }
            }
        } else {
            actionMsg.doAction = true;
        }
        if (invalidDateEvTypeStr) actionMsg.message = invalidDateEvTypeStr + " in berth call '" + moveToBerthCall.name + "'";
        else actionMsg.message = 'Required event types ' + reqEvTypeStr + " missing in berth call '" + moveToBerthCall.name + "'";
        return actionMsg;
    };

    // Find all available event type groups/event types for list of event type groups, with check for conditionally mandatory event types
    const getAllAvailableWithConditionaMandatoryEventTypeGroups = (
        evTypeGroupArray: EventTypeGroup[],
        eventArray: TankersEvent[],
        eventTypeData: EventTypeData,
        isRetroactiveCargoModeEnabled = false,
        node: IRotationNode | null = null
    ): EventTypeGroup[] => {
        const evTypeGroupReturn: EventTypeGroup[] = [];
        if (evTypeGroupArray && evTypeGroupArray.length > 0) {
            evTypeGroupArray.forEach((item) => {
                if (isConditionalMandatoryOk(item, eventArray, eventTypeData, isRetroactiveCargoModeEnabled, node)) {
                    evTypeGroupReturn.push(item);
                }
            });
        }
        return evTypeGroupReturn;
    };

    const isRequiredParentOk = (evTypeGroup: EventTypeGroup, eventArray: TankersEvent[], node: IRotationNode): boolean => {
        let include = true;
        let keepGoing = true;
        let reqEvTypeGroup: EventTypeGroup = evTypeGroup;
        while (keepGoing) {
            const reqPrevTypeId = getRequiredPreviousTypeId(reqEvTypeGroup, node.firstTimeInPortCall);
            const reqPrevEv = eventArray.find((event) => event.eventTypeId === reqPrevTypeId);
            if (!reqPrevEv) {
                keepGoing = false;
            } else if (reqEvTypeGroup.notAfterEventTypeId) {
                const reqNotAfter = eventArray.find((event) => event.eventTypeId === reqEvTypeGroup.notAfterEventTypeId);
                keepGoing = false;
                include = Boolean(!reqNotAfter);
            } else {
                reqEvTypeGroup = reqPrevEv.eventType.eventTypeGroup;
            }
        }
        return include;
    };

    // Find all available event type groups/event types for list of event type groups, with check for required previous event types
    // Exclude event types groups dependent on other event type groups, i.e. with both requiredPreviousTypeId and notAfterEventTypeId set
    const getAllAvailableWithRequiredEventTypeGroups = (
        evTypeGroupArrayInput: EventTypeGroup[],
        eventArray: TankersEvent[],
        selectedNode: IRotationNode
    ): EventTypeGroup[] => {
        const evTypeGroupReturn: EventTypeGroup[] = [];

        // Handle null selectedNode gracefully
        if (!selectedNode) {
            return evTypeGroupReturn;
        }

        if (evTypeGroupArrayInput && evTypeGroupArrayInput.length > 0) {
            evTypeGroupArrayInput.forEach((evTypeGroup) => {
                const reqPrevTypeId = getRequiredPreviousTypeId(evTypeGroup, selectedNode.firstTimeInPortCall);
                if (!reqPrevTypeId || isRequiredEventTypeInSameEventTypeGroup(evTypeGroup, selectedNode)) {
                    evTypeGroupReturn.push(evTypeGroup);
                } else if (isRequiredParentOk(evTypeGroup, eventArray, selectedNode)) {
                    // Find required event for actual group
                    const reqEv = eventArray.find((event) => event.eventTypeId === reqPrevTypeId);
                    if (reqEv && reqEv.eventType && !isConcurrent(evTypeGroup, selectedNode.firstTimeInPortCall)) {
                        const eventListForReqParentGroup: TankersEvent[] = eventArray.filter((ev) => {
                            return (
                                ev.eventType &&
                                ev.eventType.eventTypeGroupId === reqEv.eventType.eventTypeGroupId &&
                                entityOk(ev, selectedNode, false)
                            );
                        });
                        if (eventListForReqParentGroup && eventListForReqParentGroup.length > 0) {
                            if (!evTypeGroup.notAfterEventTypeId) {
                                // Find all sequences
                                // Find a sequence with no missing events
                                // Add ev type group
                                let seqNrArray: number[] = eventListForReqParentGroup.flatMap((o) => o.concurrentSequenceNumber);
                                seqNrArray = seqNrArray.filter(function (v, i) {
                                    return seqNrArray.indexOf(v) === i;
                                });
                                if (isMultiple(reqEv.eventType.eventTypeGroup, selectedNode.firstTimeInPortCall)) {
                                    const evArrayForSeq = eventListForReqParentGroup.filter(
                                        (ev) => ev.concurrentSequenceNumber === seqNrArray[seqNrArray.length - 1]
                                    );
                                    const ind = findMissingEventTypeInSequence(evArrayForSeq);
                                    if (ind === -1) {
                                        evTypeGroupReturn.push(evTypeGroup);
                                    }
                                } else {
                                    evTypeGroupReturn.push(evTypeGroup);
                                }

                                // var keepGoing = true;
                                // var indSeq = 0;
                                // while (keepGoing) {
                                //     // For each sequence, add a parent list if sequence is not finished
                                //     const evArrayForSeq = eventListForReqParentGroup.filter(
                                //         (ev) => ev.concurrentSequenceNumber === seqNrArray[indSeq]
                                //     );
                                //     const ind = findMissingEventTypeInSequence(evArrayForSeq);
                                //     if (ind === -1) {
                                //         evTypeGroupReturn.push(evTypeGroup);
                                //         keepGoing = false;
                                //     } else {
                                //         indSeq++;
                                //         if (indSeq === seqNrArray.length) {
                                //             keepGoing = false;
                                //         }
                                //     }
                                // }
                            } else {
                                if (!isMultiple(evTypeGroup, selectedNode.firstTimeInPortCall)) {
                                    // Required events exists, find all events in the required event type group
                                    const ind = findMissingEventTypeInSequence(eventListForReqParentGroup);
                                    if (ind !== -1) {
                                        // Find all items in sequence
                                        const seqArray = eventListForReqParentGroup.filter(
                                            (seqItem) =>
                                                seqItem.eventTypeGroupSequenceIdentity ===
                                                eventListForReqParentGroup[ind].eventTypeGroupSequenceIdentity
                                        );
                                        if (seqArray && seqArray.length > 0) {
                                            if (seqArray.find((ev) => ev.eventTypeId === reqPrevTypeId)) {
                                                evTypeGroupReturn.push(evTypeGroup);
                                            }
                                        }
                                    } else {
                                        if (eventListForReqParentGroup.find((ev) => ev.eventTypeId === reqPrevTypeId)) {
                                            evTypeGroupReturn.push(evTypeGroup);
                                        }
                                    }
                                } else {
                                    evTypeGroupReturn.push(evTypeGroup);
                                }
                            }
                        }
                    }
                }
            });
        }
        return evTypeGroupReturn;
    };

    // Find started and not finished parent event type group, check for requiredPreviousTypeId and notAfterEventTypeId
    const hasStartedNotFinishedParentEventTypeGroup = (
        eventArray: TankersEvent[],
        parentEventTypeGroup: EventTypeGroup | null,
        requiredPreviousTypeId: number,
        notAfterEventTypeId: number | null
    ): boolean => {
        let hasStartedNotFinishedParent = false;
        // Find all events for actual group, level and entity
        const eventListForParentGroup: TankersEvent[] = eventArray.filter((event) => {
            return event.eventType && parentEventTypeGroup && event.eventType.eventTypeGroupId === parentEventTypeGroup.eventTypeGroupId;
        });
        if (eventListForParentGroup && eventListForParentGroup.length > 0) {
            let seqIdArray: string[] = eventListForParentGroup.flatMap((o) => o.eventTypeGroupSequenceIdentity);
            seqIdArray = seqIdArray.filter(function (v, i) {
                return seqIdArray.indexOf(v) === i;
            });
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                // Find all items in sequence
                const seqArray = eventListForParentGroup.filter((seqItem) => seqItem.eventTypeGroupSequenceIdentity === seqIdArray[ind]);
                if (seqArray && seqArray.length > 0) {
                    const req = seqArray.find((seqItem) => seqItem.eventTypeId === requiredPreviousTypeId);
                    if (parentEventTypeGroup.eventTypeArray.length === 1) {
                        if (req) {
                            hasStartedNotFinishedParent = true;
                        }
                    } else {
                        const seqInd = findMissingEventTypeInSequence(seqArray);
                        if (seqInd !== -1) {
                            if (req) {
                                if (notAfterEventTypeId) {
                                    const notAfter = seqArray.find((seqItem) => seqItem.eventTypeId === notAfterEventTypeId);
                                    if (!notAfter) {
                                        hasStartedNotFinishedParent = true;
                                    }
                                } else {
                                    hasStartedNotFinishedParent = true;
                                }
                            }
                        } else if (!notAfterEventTypeId) {
                            hasStartedNotFinishedParent = true;
                        }
                    }
                }
                if (hasStartedNotFinishedParent) {
                    keepGoing = false;
                } else {
                    ind++;
                    keepGoing = ind < seqIdArray.length;
                }
            }
        }
        return hasStartedNotFinishedParent;
    };

    const isAfterFinishedParentEventTypeGroup = (
        eventArray: TankersEvent[],
        parentEventTypeGroup: EventTypeGroup | null,
        requiredPreviousTypeId: number
    ): boolean => {
        let hasFinishedParent = false;
        // Find all events for actual group, level and entity
        const eventListForParentGroup: TankersEvent[] = eventArray.filter((event) => {
            return event.eventType && parentEventTypeGroup && event.eventType.eventTypeGroupId === parentEventTypeGroup.eventTypeGroupId;
        });
        if (eventListForParentGroup && eventListForParentGroup.length > 0) {
            let seqIdArray = eventListForParentGroup.flatMap((o) => o.concurrentSequenceNumber);
            seqIdArray = seqIdArray.filter(function (v, i) {
                return seqIdArray.indexOf(v) === i;
            });
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                // Find all items in sequence
                const seqArray = eventListForParentGroup.filter((seqItem) => seqItem.concurrentSequenceNumber === seqIdArray[ind]);
                if (seqArray && seqArray.length > 0) {
                    const req = seqArray.find((seqItem) => seqItem.eventTypeId === requiredPreviousTypeId);
                    if (parentEventTypeGroup.eventTypeArray.length === 1) {
                        if (req) {
                            hasFinishedParent = true;
                            keepGoing = false;
                        }
                    } else {
                        const seqInd = findMissingEventTypeInSequence(seqArray);
                        if (seqInd === -1) {
                            hasFinishedParent = true;
                            keepGoing = false;
                        }
                    }
                }
                ind++;
                if (ind === seqIdArray.length) keepGoing = false;
            }
        }
        return hasFinishedParent;
    };

    // Find started and not finished parent event type group, check for requiredPreviousTypeId and notAfterEventTypeId
    const isAfterStartedNotFinishedParentEventTypeGroup = (
        eventArray: TankersEvent[],
        checkEvent: TankersEvent,
        parentEventTypeGroup: EventTypeGroup | null,
        requiredPreviousTypeId: number
    ): MoveActionMsg => {
        const actionMsg = new MoveActionMsg(false, null, true, null, null, null);
        // let hasStartedNotFinishedParent = false;
        // Find all events for actual group, level and entity
        const eventListForParentGroup: TankersEvent[] = eventArray.filter((event) => {
            return event.eventType && parentEventTypeGroup && event.eventType.eventTypeGroupId === parentEventTypeGroup.eventTypeGroupId;
        });
        if (eventListForParentGroup && eventListForParentGroup.length > 0) {
            let seqIdArray: string[] = eventListForParentGroup.flatMap((o) => o.eventTypeGroupSequenceIdentity);
            seqIdArray = seqIdArray.filter(function (v, i) {
                return seqIdArray.indexOf(v) === i;
            });
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                const seqArray = eventListForParentGroup.filter((seqItem) => seqItem.eventTypeGroupSequenceIdentity === seqIdArray[ind]);
                if (seqArray && seqArray.length > 0) {
                    const requiredEv = seqArray.find((seqItem) => seqItem.eventTypeId === requiredPreviousTypeId);
                    if (requiredEv) {
                        if (isDateAfter(getDateWithoutSecMs(requiredEv.eventDate.actualDateTimeUtc), checkEvent.eventDate.actualDateTimeUtc)) {
                            actionMsg.doAction = false;
                            actionMsg.message =
                                "'" +
                                checkEvent.eventType.name +
                                generateDisplayName(checkEvent.dependencyDisplayName) +
                                " must be after '" +
                                requiredEv.eventType.name +
                                "'";
                        } else {
                            actionMsg.doAction = true;
                        }
                        keepGoing = false;
                    }
                }
                if (keepGoing) {
                    ind++;
                    keepGoing = ind < seqIdArray.length;
                }
            }
        }
        return actionMsg;
    };

    // Find all available event type groups/event types for list of event type groups, with check for sequence dependency
    const getAllAvailableWithParentEventTypeGroups = (
        evTypeGroupArrayInput: EventTypeGroup[],
        eventArray: TankersEvent[],
        eventTypeData: EventTypeData,
        node: IRotationNode
    ): EventTypeGroup[] => {
        const evTypeGroupReturn: EventTypeGroup[] = [];

        // Handle null node gracefully
        if (!node) {
            return evTypeGroupReturn;
        }

        if (evTypeGroupArrayInput && evTypeGroupArrayInput.length > 0) {
            evTypeGroupArrayInput.forEach((evTypeGroup) => {
                const reqPrevTypeId = getRequiredPreviousTypeId(evTypeGroup, node.firstTimeInPortCall);
                if (reqPrevTypeId && getNotAfterEventTypeId(evTypeGroup, node.firstTimeInPortCall)) {
                    const parentEventTypeGroup = getParentEventTypeGroup(reqPrevTypeId, eventTypeData);
                    if (
                        hasStartedNotFinishedParentEventTypeGroup(
                            eventArray,
                            parentEventTypeGroup,
                            reqPrevTypeId,
                            getNotAfterEventTypeId(evTypeGroup, node.firstTimeInPortCall)
                        )
                    ) {
                        evTypeGroupReturn.push(evTypeGroup);
                    }
                } else if (reqPrevTypeId && !getNotAfterEventTypeId(evTypeGroup, node.firstTimeInPortCall)) {
                    const parentEventTypeGroup = getParentEventTypeGroup(reqPrevTypeId, eventTypeData);
                    if (isAfterFinishedParentEventTypeGroup(eventArray, parentEventTypeGroup, reqPrevTypeId)) {
                        evTypeGroupReturn.push(evTypeGroup);
                    }
                }
            });
        }
        return evTypeGroupReturn;
    };

    // Find dependent mandatory event type groups for rotation entities without stored events
    const findRotationEntityWithMissingEvents = (
        dependentEvTypeGroup: EventTypeGroup,
        eventArray: TankersEvent[],
        selectedNode: IRotationNode
    ): boolean => {
        let missingEvent = false;

        switch (selectedNode.type) {
            case RotationTreeEnum.PortCall: {
                // From selectedNode, get context
                const portCall = getRequiredContextObj(selectedNode, selectedNode.type) as PortCall;
                if (dependentEvTypeGroup.eventTypeLevel.eventTypeLevelId === RotationTreeEnum.BerthCall) {
                    let berthCallIdArray: number[] = portCall.berthCallArray.flatMap((o) => o.berthCallId);
                    berthCallIdArray = berthCallIdArray.filter(function (v, i) {
                        return berthCallIdArray.indexOf(v) === i;
                    });
                    berthCallIdArray.forEach((berthCallIdItem) => {
                        const missEv = eventArray.find((ev) => ev.berthCallId === berthCallIdItem);
                        if (!missEv) {
                            missingEvent = true;
                        }
                    });
                } else if (dependentEvTypeGroup.eventTypeLevel.eventTypeLevelId === RotationTreeEnum.CargoOperation) {
                    portCall.berthCallArray.forEach((berthCallItem) => {
                        // const cargoOperationIdArray: number[] = _.uniq(_.pluck(berthCallItem.cargoOperationArray, 'cargoOperationId'));
                        let cargoOperationIdArray: number[] = berthCallItem.cargoOperationArray.flatMap((o) => o.cargoOperationId);
                        cargoOperationIdArray = cargoOperationIdArray.filter(function (v, i) {
                            return cargoOperationIdArray.indexOf(v) === i;
                        });
                        cargoOperationIdArray.forEach((cargoOperationIdItem) => {
                            const missEv = eventArray.find((ev) => ev.cargoOperationId === cargoOperationIdItem);
                            if (!missEv) {
                                missingEvent = true;
                            }
                        });
                    });
                }
                break;
            }
            case RotationTreeEnum.BerthCall: {
                // From selectedNode, get context
                const berthCall = getRequiredContextObj(selectedNode, selectedNode.type) as BerthCall;
                if (dependentEvTypeGroup.eventTypeLevel.eventTypeLevelId === RotationTreeEnum.CargoOperation) {
                    // const cargoOperationIdArray: number[] = _.uniq(_.pluck(berthCall.cargoOperationArray, 'cargoOperationId'));
                    let cargoOperationIdArray: number[] = berthCall.cargoOperationArray.flatMap((o) => o.cargoOperationId);
                    cargoOperationIdArray = cargoOperationIdArray.filter(function (v, i) {
                        return cargoOperationIdArray.indexOf(v) === i;
                    });
                    cargoOperationIdArray.forEach((cargoOperationIdItem) => {
                        const missEv = eventArray.find((ev) => ev.cargoOperationId === cargoOperationIdItem);
                        if (!missEv) {
                            missingEvent = true;
                        }
                    });
                }
                break;
            }
            case RotationTreeEnum.CargoOperation:
                break;
        }
        return missingEvent;
    };

    // Checks if the context node is empty, i.e. a port call without berth calls, or a berth call without cargo operations
    const isContextNodeEmpty = (dependentEvTypeGroup: EventTypeGroup, selectedNode: IRotationNode): boolean => {
        let nodeIsEmpty = false;

        switch (selectedNode.type) {
            case RotationTreeEnum.PortCall: {
                // From selectedNode, get context
                const portCall = getRequiredContextObj(selectedNode, selectedNode.type) as PortCall;
                if (dependentEvTypeGroup.eventTypeLevel.eventTypeLevelId === RotationTreeEnum.BerthCall) {
                    nodeIsEmpty = portCall.berthCallArray.length === 0;
                } else if (dependentEvTypeGroup.eventTypeLevel.eventTypeLevelId === RotationTreeEnum.CargoOperation) {
                    if (portCall.berthCallArray.length > 0) {
                        let keepGoing = true;
                        let ind = 0;
                        while (keepGoing) {
                            if (portCall.berthCallArray[ind].cargoOperationArray && portCall.berthCallArray[ind].cargoOperationArray.length === 0) {
                                nodeIsEmpty = true;
                                keepGoing = false;
                            } else {
                                ind++;
                                keepGoing = ind < portCall.berthCallArray.length;
                            }
                        }
                    } else {
                        nodeIsEmpty = true;
                    }
                }
                break;
            }
            case RotationTreeEnum.BerthCall: {
                // From selectedNode, get context
                const berthCall = getRequiredContextObj(selectedNode, selectedNode.type) as BerthCall;
                if (dependentEvTypeGroup.eventTypeLevel.eventTypeLevelId === RotationTreeEnum.CargoOperation) {
                    nodeIsEmpty = berthCall.cargoOperationArray.length === 0;
                }
                break;
            }
            case RotationTreeEnum.CargoOperation:
                break;
        }
        return nodeIsEmpty;
    };

    // Generate the display name for event toolbox and event list
    const getEventTypeNameForToolbox = (
        eventArray: TankersEvent[],
        seqArray: TankersEvent[],
        event: TankersEvent | null,
        eventType: EventType,
        dropdownValuesExist: boolean,
        seqCounter: number,
        eventTypeData: EventTypeData,
        node: IRotationNode
    ): string => {
        // Use the resolved name (applies date-based overrides for eventTypeId 3 - SOSP -> BOSP)
        let displayName: string = event ? resolveEventTypeName(eventType, event) : resolveEventTypeNameForNewEvent(eventType);
        const dispName = generateDependencyDisplayName(eventArray, seqArray, event, eventType, dropdownValuesExist, seqCounter, eventTypeData, node);
        displayName += dispName ? ' ' + dispName : '';
        return displayName;
    };

    const createEventName = (eventArray: TankersEvent[], event: TankersEvent, eventTypeData: EventTypeData, node: IRotationNode): string => {
        const seqEvArray = eventArray.filter((seqItem) => seqItem.eventTypeGroupSequenceIdentity === event.eventTypeGroupSequenceIdentity);
        if (seqEvArray && seqEvArray.length > 0) {
            return getEventTypeNameForToolbox(eventArray, seqEvArray, event, event.eventType, doesDropdownValueExist(event), 1, eventTypeData, node);
        }
        // Use the resolved name (applies date-based overrides)
        return resolveEventTypeName(event.eventType, event);
    };

    const hasAfterDependentFinished = (
        eventArray: TankersEvent[],
        dependentTypeGroup: EventTypeGroup,
        node: IRotationNode,
        seqId: number
    ): boolean => {
        let hasDependentEvType = false;
        const sortedEvTypeList = sortOrderedEventTypes(dependentTypeGroup.eventTypeArray);
        const depEvListAll = eventArray.filter(
            (ev) =>
                !getNotAfterEventTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) &&
                getRequiredPreviousTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) ===
                    sortedEvTypeList[sortedEvTypeList.length - 1].eventTypeId &&
                ev.concurrentSequenceNumber === seqId
        );

        if (depEvListAll && depEvListAll.length > 0) {
            let evTypeGroupIdArray = depEvListAll.flatMap((o) => o.eventType.eventTypeGroupId);
            evTypeGroupIdArray = evTypeGroupIdArray.filter(function (v, i) {
                return evTypeGroupIdArray.indexOf(v) === i;
            });
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                const depEvList = depEvListAll.filter((ev) => ev.eventType.eventTypeGroupId === evTypeGroupIdArray[ind]);
                if (depEvList && depEvList.length > 0) {
                    const ind2 = findMissingEventTypeInSequence(depEvList);
                    if (ind2 !== -1) {
                        keepGoing = false;
                        hasDependentEvType = true;
                    }
                }
                ind++;
                if (ind === evTypeGroupIdArray.length) keepGoing = false;
            }
        }
        return hasDependentEvType;
    };

    // Check if event type group should be added
    const shouldEventTypeGroupBeAdded = (
        eventArray: TankersEvent[],
        dependentTypeGroup: EventTypeGroup,
        node: IRotationNode,
        parentSequenceId: string
    ) => {
        let addEventTypeGroup = true;

        // Find event within same event type group and with same sequence number as the event type group dependent on the not finished event type group
        // Find all events for actual group, level and entity
        const eventListForDependentGroup: TankersEvent[] = eventArray.filter((event) => {
            return (
                event.eventType.eventTypeGroupId === dependentTypeGroup.eventTypeGroupId &&
                event.eventTypeGroupSequenceDependencyIdentity === parentSequenceId &&
                entityOk(event, node, true)
            );
        });
        if (dependentTypeGroup) {
            if (eventListForDependentGroup && eventListForDependentGroup.length > 0) {
                const ind2 = findMissingEventTypeInSequence(eventListForDependentGroup);
                if (ind2 !== -1) {
                    addEventTypeGroup = false;
                } else if (isEtgMandatory(dependentTypeGroup, node)) {
                    // Find dependent mandatory event type groups for rotation entities without stored events
                    if (findRotationEntityWithMissingEvents(dependentTypeGroup, eventArray, node)) {
                        addEventTypeGroup = false;
                    } else if (
                        hasAfterDependentFinished(eventArray, dependentTypeGroup, node, eventListForDependentGroup[0].concurrentSequenceNumber)
                    ) {
                        addEventTypeGroup = false;
                    }
                }
            } else if (isEtgMandatory(dependentTypeGroup, node)) {
                // No events in the dependent event type group, but event type group is optional
                addEventTypeGroup = false;
            }
        }
        return addEventTypeGroup;
    };

    const findConditionalMandatoryIds = (eventTypeData: EventTypeData, eventTypeGroup: EventTypeGroup, node: IRotationNode): number[] => {
        const condMandIdArray: number[] = [];
        if (eventTypeData) {
            // Go through all elements in conditionalMandatoryList
            eventTypeData.conditionalMandatoryArray.forEach((item) => {
                // Find conditional mandatory event type group (e.g. Berth)
                const dependentTypeGroup = getEventTypeGroup(eventTypeData, item.eventTypeGroupId, eventTypeGroup.eventTypeLevel.eventTypeLevelId);
                if (dependentTypeGroup) {
                    // Go through all event types for parent event type groups (e.g. Sea Passage), check if there are any required
                    const reqPrevTypeId = getRequiredPreviousTypeId(dependentTypeGroup, node.firstTimeInPortCall);
                    const evType = eventTypeGroup.eventTypeArray.find((etg) => etg.eventTypeId === reqPrevTypeId);
                    if (evType) {
                        condMandIdArray.push(item.conditionalMandatoryIdentity);
                    }
                }
            });
        }
        return condMandIdArray.filter(function (v, i) {
            return condMandIdArray.indexOf(v) === i;
        });
    };

    const verifyConditionalMandatory = (
        newEvType: EventType,
        eventArray: TankersEvent[],
        seqIdItem: string,
        selectedNode: IRotationNode,
        eventTypeData: EventTypeData
    ): boolean => {
        // Check if the event type group has conditionally mandatory dependent event type groups (e.g. Berth/Anchorage cargo ops)
        // If no events stored in any conditionally mandatory dependent group, add event type
        // If one of the conditionally dependent groups is started, add event if NotAfterEventTypeId is not registered
        // If one of the conditionally mandatory dependent group is completed, add the event
        let addEventType = true;
        let allNodesEmpty = true;
        let hasCondMand = false;
        const reqCondArray = findConditionalMandatoryIds(eventTypeData, newEvType.eventTypeGroup, selectedNode);
        if (reqCondArray && reqCondArray.length > 0) {
            let keepGoingCondId = true;
            let condIdInd = 0;
            while (keepGoingCondId) {
                let condMandGroupArray: ConditionalMandatory[] = [];
                if (eventTypeData) {
                    condMandGroupArray = eventTypeData.conditionalMandatoryArray.filter(
                        (cmItem) => cmItem.conditionalMandatoryIdentity === reqCondArray[condIdInd]
                    );
                }
                if (condMandGroupArray && condMandGroupArray.length > 0) {
                    let keepGoingCondMand = true;
                    let condMandInd = 0;
                    while (keepGoingCondMand) {
                        // E.g. depTypeGroup = Berth
                        const depTypeGroup = getEventTypeGroup(
                            eventTypeData,
                            condMandGroupArray[condMandInd].eventTypeGroupId,
                            newEvType.eventTypeGroup.eventTypeLevel.eventTypeLevelId
                        );

                        if (depTypeGroup && !isContextNodeEmpty(depTypeGroup, selectedNode)) {
                            // Check for e.g SOSP
                            if (depTypeGroup.notAfterEventTypeId === newEvType.eventTypeId) {
                                // Get dependent events, e.g for Berth
                                const eventListForDependentGroup: TankersEvent[] = eventArray.filter((event) => {
                                    return (
                                        event.eventType &&
                                        event.eventType.eventTypeGroupId === depTypeGroup.eventTypeGroupId &&
                                        event.eventTypeGroupSequenceDependencyIdentity === seqIdItem &&
                                        entityOk(event, selectedNode, true)
                                    );
                                });
                                hasCondMand = true;
                                if (eventListForDependentGroup && eventListForDependentGroup.length > 0) {
                                    allNodesEmpty = false;
                                    const ind2 = findMissingEventTypeInSequence(eventListForDependentGroup);
                                    // Find dependent mandatory event type groups for rotation entities without stored events
                                    if (ind2 !== -1 || findRotationEntityWithMissingEvents(depTypeGroup, eventArray, selectedNode)) {
                                        addEventType = false;
                                    }
                                }
                            }
                        }
                        if (!addEventType || condMandInd === condMandGroupArray.length - 1) keepGoingCondMand = false;
                        else condMandInd++;
                    }
                }
                if (!addEventType || condIdInd === reqCondArray.length - 1) keepGoingCondId = false;
                else condIdInd++;
            }
        }
        return addEventType && (!hasCondMand || (hasCondMand && !allNodesEmpty));
    };

    const verifyDependentEventTypeGroup = (
        newEvType: EventType,
        eventArray: TankersEvent[],
        seqIdItem: string,
        selectedNode: IRotationNode,
        eventTypeData: EventTypeData
    ): boolean => {
        let depEvTypeGroupVerified = false;
        const dependentTypeGroupArray = getDependentEventTypeGroupArray(newEvType, eventTypeData);
        if (dependentTypeGroupArray && dependentTypeGroupArray.length > 0) {
            let addEventTypeGroup = true;
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                if (!isContextNodeEmpty(dependentTypeGroupArray[ind], selectedNode)) {
                    if (!shouldEventTypeGroupBeAdded(eventArray, dependentTypeGroupArray[ind], selectedNode, seqIdItem)) {
                        keepGoing = false;
                        addEventTypeGroup = false;
                    } else {
                        ind++;
                        keepGoing = ind < dependentTypeGroupArray.length;
                    }
                } else {
                    ind++;
                    keepGoing = ind < dependentTypeGroupArray.length;
                }
            }
            depEvTypeGroupVerified = addEventTypeGroup;
        } else {
            if (newEvType && newEvType.eventTypeGroupRequiredArray && newEvType.eventTypeGroupRequiredArray.length > 0) {
                if (!missingRequiredEventTypeGroup(newEvType, eventArray)) {
                    depEvTypeGroupVerified = true;
                }
            } else {
                depEvTypeGroupVerified = true;
            }
        }
        return depEvTypeGroupVerified;
    };

    const verifyRequiredConditionalMandatory = (
        newEvType: EventType,
        eventArray: TankersEvent[],
        seqIdItem: string,
        selectedNode: IRotationNode,
        eventTypeData: EventTypeData
    ): boolean => {
        let reqCondMandatoryVerified = true;
        let keepGoingLevel = true;
        let indLevel = 0;
        while (keepGoingLevel) {
            let keepGoingGroup = true;
            let indGroup = 0;
            while (keepGoingGroup) {
                // Check if event type group has required conditional mandatory (e.g. Loading Master and Cargo Hose)
                if (
                    eventTypeData &&
                    eventTypeData.eventTypeLevelArray &&
                    eventTypeData.eventTypeLevelArray.length > indLevel &&
                    eventTypeData.eventTypeLevelArray[indLevel].eventTypeGroupArray &&
                    eventTypeData.eventTypeLevelArray[indLevel].eventTypeGroupArray.length > 0 &&
                    eventTypeData.eventTypeLevelArray[indLevel].eventTypeGroupArray[indGroup].requiredConditionalMandatoryArray &&
                    eventTypeData.eventTypeLevelArray[indLevel].eventTypeGroupArray[indGroup].requiredConditionalMandatoryArray.length > 0
                ) {
                    // Check if required conditional mandatory is equal to current
                    const reqCondMand = eventTypeData.eventTypeLevelArray[indLevel].eventTypeGroupArray[
                        indGroup
                    ].requiredConditionalMandatoryArray.find((item) => item.notAfterEventTypeId === newEvType.eventTypeId);
                    if (reqCondMand && !isContextNodeEmpty(eventTypeData.eventTypeLevelArray[indLevel].eventTypeGroupArray[indGroup], selectedNode)) {
                        if (
                            !shouldEventTypeGroupBeAdded(
                                eventArray,
                                eventTypeData.eventTypeLevelArray[indLevel].eventTypeGroupArray[indGroup],
                                selectedNode,
                                seqIdItem
                            )
                        ) {
                            keepGoingGroup = false;
                            reqCondMandatoryVerified = false;
                        } else {
                            indGroup++;
                            keepGoingGroup = indGroup < eventTypeData.eventTypeLevelArray[indLevel].eventTypeGroupArray.length;
                        }
                    } else {
                        indGroup++;
                        keepGoingGroup = indGroup < eventTypeData.eventTypeLevelArray[indLevel].eventTypeGroupArray.length;
                    }
                } else {
                    indGroup++;
                    keepGoingGroup = indGroup < eventTypeData.eventTypeLevelArray[indLevel].eventTypeGroupArray.length;
                }
            }
            if (reqCondMandatoryVerified) {
                indLevel++;
                keepGoingLevel = indLevel < eventTypeData.eventTypeLevelArray.length;
            } else {
                keepGoingLevel = false;
            }
        }
        return reqCondMandatoryVerified;
    };

    const verifyAlternateEventTypeGroup = (newEvType: EventType, eventArray: TankersEvent[], eventTypeData: EventTypeData): boolean => {
        let verified = true;
        // Check if newEvType has entries in AlternateEventTypeGroupMap
        const altEvTypeGrIdList = eventTypeData.alternateEventTypeGroupMapList
            .filter((aetgm) => aetgm.eventTypeGroupId === newEvType.eventTypeGroupId)
            .map((o) => o.alternateEventTypeGroupMapIdentity);
        if (altEvTypeGrIdList && altEvTypeGrIdList.length > 0) {
            const altEvTypeGrIdListUnique = altEvTypeGrIdList.filter(function (v, i) {
                return altEvTypeGrIdList.indexOf(v) === i;
            });
            // For each entry in AlternateEventTypeGroupMap
            let ind = 0;
            let keepGoing = true;
            while (keepGoing) {
                // Find all entries in AlternateEventTypeGroupMap for AlternateEventTypeGroupMapIdentity
                const altEvTypeGroupMapList = eventTypeData.alternateEventTypeGroupMapList.filter(
                    (o) => o.alternateEventTypeGroupMapIdentity === altEvTypeGrIdListUnique[ind]
                );
                if (altEvTypeGroupMapList && altEvTypeGroupMapList.length > 0) {
                    // Find all events for all event type groups in all entries of altEvTypeGroupMapList
                    const evList = eventArray
                        .filter((ev) => {
                            return altEvTypeGroupMapList.find((o) => o.eventTypeGroupId === ev.eventType?.eventTypeGroupId);
                        })
                        .sort((a, b) => compareActualEventDateTime(a, b, false));
                    if (evList && evList.length > 0) {
                        // Check the last event of all events in 'evList'
                        if (evList[evList.length - 1].eventType?.eventTypeGroupId === newEvType.eventTypeGroupId) {
                            verified = false;
                        }
                    } else {
                        // Check initial event type groups
                        const evListInitial = eventArray
                            .filter((ev) => {
                                return altEvTypeGroupMapList.find((o) => {
                                    return o.initialExcludeEventTypeGroupId && o.initialExcludeEventTypeGroupId === ev.eventType?.eventTypeGroupId;
                                });
                            })
                            .sort((a, b) => compareActualEventDateTime(a, b, false));
                        if (evListInitial && evListInitial.length > 0) {
                            const altEvTypeGroupMapInitial = altEvTypeGroupMapList.find((o) => o.eventTypeGroupId === newEvType.eventTypeGroupId);
                            if (altEvTypeGroupMapInitial) {
                                if (
                                    evListInitial[evListInitial.length - 1].eventType?.eventTypeGroupId ===
                                    altEvTypeGroupMapInitial.initialExcludeEventTypeGroupId
                                ) {
                                    verified = false;
                                }
                            }
                        }
                    }
                    if (!verified) keepGoing = false;
                    else {
                        ind++;
                    }
                } else {
                    ind++;
                }
                if (ind === altEvTypeGrIdListUnique.length) keepGoing = false;
            }
        }

        // Find all events for event type groups in AlternateEventTypeGroupMap item

        // If no events: check InitialExcludeEventTypeGroup
        // If InitialExcludeEventTypeGroup != null:
        // Find all events for event type groups in InitialExcludeEventTypeGroup item
        // If events: find last event
        // If EventTypeGroupId of last event == EventTypeGroupId of newEvType
        // return false

        // If events in AlternateEventTypeGroupMap: find last event
        // If EventTypeGroupId of last event == EventTypeGroupId of newEvType
        // return false

        // Otherwise:
        return verified;
    };

    const isParentConcurrent = (parentEvent: TankersEvent | null, eventArray: TankersEvent[], node: IRotationNode): boolean => {
        let addDependentEventType = false;
        if (parentEvent) {
            const parentEventArray = eventArray.filter(
                (ev) => ev.eventType && ev.eventType.eventTypeGroupId === parentEvent.eventType.eventTypeGroupId
            );
            if (parentEventArray && parentEventArray.length > 0) {
                // Get all sequences for parent event type group
                let seqParentIdArray: string[] = parentEventArray.flatMap((o) => o.eventTypeGroupSequenceIdentity);
                seqParentIdArray = seqParentIdArray.filter(function (v, i) {
                    return seqParentIdArray.indexOf(v) === i;
                });
                let keepGoing = true;
                let ind = 0;
                while (keepGoing) {
                    const seqParentArray = parentEventArray.filter((seqItem) => seqItem.eventTypeGroupSequenceIdentity === seqParentIdArray[ind]);
                    if (seqParentArray && seqParentArray.length > 0) {
                        const evInd = findMissingEventTypeInSequence(seqParentArray);
                        if (evInd !== -1) {
                            // Parent event type group is started
                            const depEvArray = eventArray.filter(
                                (ev) => ev.eventTypeGroupSequenceDependencyIdentity === seqParentArray[0].eventTypeGroupSequenceIdentity
                            );
                            if (depEvArray && depEvArray.length > 0) {
                                const ind2 = findMissingEventTypeInSequence(depEvArray);
                                if (ind2 === -1 && isMultiple(depEvArray[0].eventType.eventTypeGroup, node.firstTimeInPortCall)) {
                                    addDependentEventType = true;
                                    keepGoing = false;
                                }
                            } else {
                                addDependentEventType = true;
                                keepGoing = false;
                            }
                        }
                    }
                    if (keepGoing) {
                        ind++;
                        keepGoing = ind < seqParentIdArray.length;
                    }
                }
            }
        }
        return addDependentEventType;
    };

    const getConcurrentSequenceNumber = (
        concurrentParent: EventType | null,
        evTypeGroup: EventTypeGroup,
        eventListForGroup: TankersEvent[],
        node: IRotationNode
    ): number | null => {
        if (isConcurrent(evTypeGroup, node.firstTimeInPortCall)) {
            if (eventListForGroup && eventListForGroup.length > 0) {
                return null;
            } else {
                return 1;
            }
        }
        return null;
    };

    const hasDependentNotCompletedChildren = (
        newEvType: EventType,
        eventArray: TankersEvent[],
        eventTypeData: EventTypeData,
        node: IRotationNode
    ): boolean => {
        let hasDependent = false;
        const grArray = eventTypeData.eventTypeLevelArray.flatMap((o) => o.eventTypeGroupArray);
        const etGrList = grArray.filter((et) => getRequiredPreviousTypeId(et, node.firstTimeInPortCall) === newEvType.eventTypeId);
        if (etGrList && etGrList.length > 0) {
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                // Check if unfinished sequence or multiple
                const eventListForGroup: TankersEvent[] = eventArray.filter((event) => {
                    return event.eventType && event.eventType.eventTypeGroupId === etGrList[ind].eventTypeGroupId;
                });
                if (isMultiple(etGrList[ind], node.firstTimeInPortCall) || !eventListForGroup || eventListForGroup.length === 0) {
                    hasDependent = true;
                    keepGoing = false;
                } else {
                    const ind = findMissingEventTypeInSequence(eventListForGroup);
                    if (ind !== -1) {
                        hasDependent = true;
                        keepGoing = false;
                    }
                }
                if (keepGoing) {
                    ind++;
                    keepGoing = ind < etGrList.length;
                }
            }
        }
        return hasDependent;
    };

    const hasParentNotFinishedAndGroupNotDependent = (
        eventArray: TankersEvent[],
        eventListForGroup: TankersEvent[],
        reqParentEvType: EventTypeGroup
    ): boolean => {
        // Find parent events
        const evParentArray = eventArray.filter(
            (ev: TankersEvent) => ev.eventType && reqParentEvType && ev.eventType.eventTypeGroupId === reqParentEvType.eventTypeGroupId
        );
        const ind = findMissingEventTypeInSequence(evParentArray);
        if (ind !== -1) {
            // Parent event type group not finished
            // Is there any events in the group dependent on not finished event type parent group
            const evListForGroup = eventListForGroup.find(
                (ev) => ev.eventTypeGroupSequenceDependencyIdentity === evParentArray[ind].eventTypeGroupSequenceIdentity
            );
            if (!evListForGroup) {
                return true;
            }
        }
        return false;
    };

    const concurrentParentIsNotFinished = (
        evTypeGroupInput: EventTypeGroup,
        eventArray: TankersEvent[],
        requiredParent: EventTypeGroup,
        evType: EventType,
        node: IRotationNode,
        eventTypeData: EventTypeData
    ): boolean => {
        let includeEvType = false;
        const evParentArray = eventArray.filter(
            (ev: TankersEvent) => ev.eventType && ev.eventType.eventTypeGroupId === requiredParent.eventTypeGroupId
        );
        let seqArray = evParentArray.flatMap((o) => o.concurrentSequenceNumber);
        seqArray = seqArray.filter(function (v, i) {
            return seqArray.indexOf(v) === i;
        });
        let keepGoing = true;
        let parentSeqInd = 0;
        while (keepGoing) {
            const evSeqArray = eventArray.filter(
                (ev: TankersEvent) =>
                    ev.eventType &&
                    ev.eventType.eventTypeGroupId === evTypeGroupInput.eventTypeGroupId &&
                    ev.concurrentSequenceNumber === seqArray[parentSeqInd]
            );
            const concurrentParent = hasConcurrentParentInCompleteTree(evType, eventTypeData, node);
            if (concurrentParent) {
                const concParentEvList = eventArray.filter(
                    (ev) =>
                        ev.eventType.eventTypeGroup.eventTypeGroupId === concurrentParent.eventTypeGroup.eventTypeGroupId &&
                        ev.concurrentSequenceNumber === seqArray[parentSeqInd]
                );
                const concParentSeqNotFinished = isSeqNotFinished(concParentEvList, concurrentParent, seqArray[parentSeqInd]);
                if (concParentSeqNotFinished && evSeqArray.length === 0) {
                    includeEvType = true;
                    keepGoing = false;
                }
            }
            parentSeqInd++;
            if (parentSeqInd === seqArray.length) keepGoing = false;
        }
        return includeEvType;
    };

    // Helper to check if we should bypass toolbox filtering for retroactive cargo mode
    const shouldBypassToolboxFiltering = (eventType: EventType | null, node: IRotationNode, isRetroactiveCargoModeEnabled: boolean): boolean => {
        return isRetroactiveCargoModeEnabled && node.type === RotationTreeEnum.CargoOperation && eventType !== null;
    };

    // Find next available event types for event group for relevant sequence (if multiple), check for all required event type groups
    // Handles concurrent event types, and returns a list of event types
    const getNextAvailableEventType = (
        evTypeGroupInput: EventTypeGroup,
        eventArray: TankersEvent[],
        node: IRotationNode,
        eventTypeData: EventTypeData,
        isRetroactiveCargoModeEnabled = false
    ): EventType[] => {
        let availableEventTypeList: EventType[] = [];
        let allEventSeqFinished = true;

        const requiredParent: EventTypeGroup | null = getRequiredEventTypeGroup(evTypeGroupInput, eventTypeData, node);
        const eventListForGroup: TankersEvent[] = eventArray.filter((event) => {
            return event.eventType && event.eventType.eventTypeGroupId === evTypeGroupInput.eventTypeGroupId && entityOk(event, node, false);
        });
        const notDeletedEventTypeArray = evTypeGroupInput.eventTypeArray.filter((et) => !et.deleted);
        const eventTypeArraySorted = sortOrderedEventTypes(notDeletedEventTypeArray);

        if (eventTypeArraySorted && eventTypeArraySorted.length > 0 && eventTypeArraySorted[0].selectable) {
            const concurrentParent = hasConcurrentParentInCompleteTree(eventTypeArraySorted[0], eventTypeData, node);
            const concurrentParentEvTypeArray = getConcurrentParentArray(eventArray, eventTypeArraySorted[0], eventTypeData, node);
            if (eventListForGroup && eventListForGroup.length > 0) {
                let seqIdArray: string[] = eventListForGroup.flatMap((o) => o.eventTypeGroupSequenceIdentity);
                seqIdArray = seqIdArray.filter(function (v, i) {
                    return seqIdArray.indexOf(v) === i;
                });
                const concurrentParent = hasConcurrentParentInCompleteTree(eventTypeArraySorted[0], eventTypeData, node);
                if (seqIdArray && seqIdArray.length > 1 && !isMultiple(evTypeGroupInput, node.firstTimeInPortCall) && !concurrentParent) {
                    const seqIdArrayTmp: string[] = [];
                    seqIdArrayTmp.push(seqIdArray[0]);
                    seqIdArray = seqIdArrayTmp;
                }
                const parentSeqNrArray: number[] = [];
                seqIdArray.forEach((seqIdItem) => {
                    const seqArray = sortOrderedEvents(eventListForGroup.filter((seqItem) => seqItem.eventTypeGroupSequenceIdentity === seqIdItem));
                    if (seqArray && seqArray.length > 0) {
                        // Check if event type group has parent event type
                        const depEvArray = eventArray.filter(
                            (ev) => ev.eventTypeGroupSequenceIdentity === seqArray[0].eventTypeGroupSequenceDependencyIdentity
                        );
                        if (concurrentParent && depEvArray && depEvArray.length > 0) {
                            parentSeqNrArray.push(depEvArray[0].concurrentSequenceNumber);
                        }
                        let ind = findMissingEventTypeInSequence(seqArray);
                        if (ind === -1) {
                            ind = seqArray.length - 1;
                        }
                        // Find index of event type for last event
                        const lastEventTypeIndex = eventTypeArraySorted.findIndex((ev) => {
                            return ev.eventTypeId === seqArray[ind].eventTypeId;
                        });
                        if (lastEventTypeIndex < eventTypeArraySorted.length - 1) {
                            allEventSeqFinished = false;
                            const newEvType = createNewEventType(eventTypeArraySorted[lastEventTypeIndex + 1]);
                            if (newEvType) {
                                newEvType.eventTypeGroup = eventTypeArraySorted[lastEventTypeIndex + 1].eventTypeGroup;
                                newEvType.eventTypeGroupSequenceIdentity = seqArray[ind].eventTypeGroupSequenceIdentity;

                                newEvType.dependencyDisplayName = seqArray[0].dependencyDisplayName;
                                if (isConcurrent(newEvType.eventTypeGroup, node.firstTimeInPortCall)) {
                                    newEvType.displayNameToolbox = getEventTypeNameForToolbox(
                                        eventArray,
                                        seqArray,
                                        null,
                                        newEvType,
                                        false,
                                        1,
                                        eventTypeData,
                                        node
                                    );
                                } else {
                                    // Use overridden name for new events
                                    newEvType.displayNameToolbox = resolveEventTypeNameForNewEvent(newEvType);
                                }
                                newEvType.parentSequenceNumber = seqArray[0].concurrentSequenceNumber;
                                if (depEvArray && depEvArray.length > 0) {
                                    newEvType.eventTypeGroupSequenceDependencyIdentity = depEvArray[0].eventTypeGroupSequenceIdentity;
                                }
                                // 🔓 RETROACTIVE CARGO MODE: Bypass toolbox filtering for cargo events
                                const shouldBypass = shouldBypassToolboxFiltering(newEvType, node, isRetroactiveCargoModeEnabled);
                                if (shouldBypass) {
                                    availableEventTypeList.push(newEvType);
                                } else if (verifyConditionalMandatory(newEvType, eventArray, seqIdItem, node, eventTypeData)) {
                                    if (verifyDependentEventTypeGroup(newEvType, eventArray, seqIdItem, node, eventTypeData)) {
                                        if (verifyRequiredConditionalMandatory(newEvType, eventArray, seqIdItem, node, eventTypeData)) {
                                            if (verifyAlternateEventTypeGroup(newEvType, eventArray, eventTypeData)) {
                                                availableEventTypeList.push(newEvType);
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                });
                const reqPrevTypeId = getRequiredPreviousTypeId(evTypeGroupInput, node.firstTimeInPortCall);
                if (concurrentParent && isEtgMandatory(evTypeGroupInput, node)) {
                    concurrentParentEvTypeArray.forEach((parentEvType) => {
                        const parentSeqNr = parentSeqNrArray.find((seqNr) => seqNr === parentEvType.parentSequenceNumber);
                        if (!parentSeqNr) {
                            availableEventTypeList.push(parentEvType);
                        }
                    });
                    if (addOptionalForMandatory(eventArray, requiredParent, evTypeGroupInput, node)) {
                        const evType = createNewEventTypeForToolbox(eventTypeArraySorted[0], null);
                        evType.actualMandatory = false;
                        availableEventTypeList.push(evType);
                    }
                } else if (
                    availableEventTypeList.length === 0 &&
                    isMultiple(evTypeGroupInput, node.firstTimeInPortCall) &&
                    allEventSeqFinished &&
                    (shouldBypassToolboxFiltering(eventTypeArraySorted[0], node, isRetroactiveCargoModeEnabled) ||
                        verifyAlternateEventTypeGroup(eventTypeArraySorted[0], eventArray, eventTypeData))
                ) {
                    const seqNr = getConcurrentSequenceNumber(concurrentParent, evTypeGroupInput, eventListForGroup, node);
                    availableEventTypeList.push(createNewEventTypeForToolbox(eventTypeArraySorted[0], seqNr));
                } else if (
                    !isMultiple(evTypeGroupInput, node.firstTimeInPortCall) &&
                    allEventSeqFinished &&
                    hasDependentNotCompletedChildren(eventTypeArraySorted[0], eventArray, eventTypeData, node)
                ) {
                    availableEventTypeList.push(createNewEventTypeForToolbox(eventTypeArraySorted[0], null));
                } else if (
                    availableEventTypeList.length === 0 &&
                    requiredParent &&
                    isMultiple(requiredParent, node.firstTimeInPortCall) &&
                    allEventSeqFinished &&
                    hasParentNotFinishedAndGroupNotDependent(eventArray, eventListForGroup, requiredParent)
                ) {
                    availableEventTypeList.push(createNewEventTypeForToolbox(eventTypeArraySorted[0], null));
                    // Find parent events
                    // const evParentArray = eventArray.filter(
                    //     (ev: TankersEvent) => ev.eventType && ev.eventType.eventTypeGroupId === requiredParent.eventTypeGroupId
                    // );
                    // const ind = findMissingEventTypeInSequence(evParentArray);
                    // if (ind !== -1) {
                    //     if (
                    //         !eventListForGroup.find(
                    //             (ev) => ev.eventTypeGroupSequenceDependencyIdentity === evParentArray[ind].eventTypeGroupSequenceIdentity
                    //         )
                    //     ) {
                    //         availableEventTypeList.push(createNewEventTypeForToolbox(eventTypeArraySorted[0], null));
                    //     }
                    // }
                } else if (
                    availableEventTypeList.length === 0 &&
                    requiredParent &&
                    allEventSeqFinished &&
                    getRequiredPreviousTypeId(evTypeGroupInput, node.firstTimeInPortCall) &&
                    !getNotAfterEventTypeId(evTypeGroupInput, node.firstTimeInPortCall) &&
                    concurrentParentIsNotFinished(evTypeGroupInput, eventArray, requiredParent, eventTypeArraySorted[0], node, eventTypeData)
                ) {
                    availableEventTypeList.push(createNewEventTypeForToolbox(eventTypeArraySorted[0], null));
                } else if (isConcurrent(evTypeGroupInput, node.firstTimeInPortCall)) {
                    const evType = createNewEventTypeForToolbox(eventTypeArraySorted[0], null);
                    evType.actualMandatory = false;
                    availableEventTypeList.push(evType);
                } else if (reqPrevTypeId && evTypeGroupInput.notAfterEventTypeId) {
                    const parentEvent: TankersEvent | null = eventArray.find((ev) => ev.eventTypeId === reqPrevTypeId) ?? null;
                    // Check if parent exist, and is concurrent
                    if (concurrentParent && isParentConcurrent(parentEvent, eventArray, node)) {
                        availableEventTypeList.push(createNewEventTypeForToolbox(eventTypeArraySorted[0], null));
                    }
                }
            } else if (isEtgMandatory(evTypeGroupInput, node)) {
                if (concurrentParentEvTypeArray.length > 0) {
                    availableEventTypeList = availableEventTypeList.concat(concurrentParentEvTypeArray);
                } else {
                    const seqNr = getConcurrentSequenceNumber(concurrentParent, evTypeGroupInput, [], node);
                    availableEventTypeList.push(createNewEventTypeForToolbox(eventTypeArraySorted[0], seqNr));
                }
            } else if (
                shouldBypassToolboxFiltering(eventTypeArraySorted[0], node, isRetroactiveCargoModeEnabled) ||
                verifyAlternateEventTypeGroup(eventTypeArraySorted[0], eventArray, eventTypeData)
            ) {
                const seqNr = getConcurrentSequenceNumber(concurrentParent, evTypeGroupInput, [], node);
                availableEventTypeList.push(createNewEventTypeForToolbox(eventTypeArraySorted[0], seqNr));
            }
        }
        return availableEventTypeList;
    };

    const addOptionalForMandatory = (
        eventArray: TankersEvent[],
        requiredParent: EventTypeGroup | null,
        evTypeGroup: EventTypeGroup,
        node: IRotationNode
    ): boolean => {
        let addDependentEventType = false;
        if (requiredParent && isEtgMandatory(evTypeGroup, node)) {
            // Find events for required parent
            const reqPrevTypeId = getRequiredPreviousTypeId(evTypeGroup, node.firstTimeInPortCall);
            const parentEvent = eventArray.find((ev) => ev.eventTypeId === reqPrevTypeId);
            if (parentEvent && parentEvent.eventType) {
                const parentEventArray = eventArray.filter(
                    (ev) => ev.eventType && ev.eventType.eventTypeGroupId === parentEvent.eventType.eventTypeGroupId
                );
                if (parentEventArray && parentEventArray.length > 0) {
                    // Get all sequences for parent event type group
                    let seqParentIdArray: string[] = parentEventArray.flatMap((o) => o.eventTypeGroupSequenceIdentity);
                    seqParentIdArray = seqParentIdArray.filter(function (v, i) {
                        return seqParentIdArray.indexOf(v) === i;
                    });

                    seqParentIdArray.forEach((seqParentIdItem) => {
                        // Find all items in sequence
                        const seqParentArray = parentEventArray.filter((seqItem) => seqItem.eventTypeGroupSequenceIdentity === seqParentIdItem);

                        if (seqParentArray && seqParentArray.length > 0) {
                            const ind = findMissingEventTypeInSequence(seqParentArray);
                            if (ind !== -1) {
                                // Parent event type group is started
                                const depEvArray = eventArray.filter(
                                    (ev) =>
                                        ev.eventType &&
                                        ev.eventType.eventTypeGroup &&
                                        ev.eventType.eventTypeGroup.eventTypeGroupId === evTypeGroup.eventTypeGroupId &&
                                        ev.eventTypeGroupSequenceDependencyIdentity === seqParentArray[0].eventTypeGroupSequenceIdentity
                                );

                                if (depEvArray && depEvArray.length > 0) {
                                    const ind2 = findMissingEventTypeInSequence(depEvArray);
                                    if (ind2 === -1) {
                                        addDependentEventType = true;
                                    }
                                }
                            }
                        }
                    });
                }
            }
        }
        return addDependentEventType;
    };

    const getConcurrentParentArray = (
        eventArray: TankersEvent[],
        eventType: EventType,
        eventTypeData: EventTypeData,
        node: IRotationNode
    ): EventType[] => {
        const eventTypeArray: EventType[] = [];

        // Check if parent exist, and is concurrent
        const parentEvType = getConcurrentParent(eventArray, eventType.eventTypeGroup, node);
        if (parentEvType) {
            const parentEventArray = eventArray.filter((ev) => ev.eventType && ev.eventType.eventTypeGroupId === parentEvType.eventTypeGroupId);
            if (parentEventArray && parentEventArray.length > 0) {
                // Get all sequences for parent event type group (e.g. Hose on)
                // const seqParentIdArray: string[] = _.uniq(_.pluck(parentEventArray, 'eventTypeGroupSequenceIdentity'));
                let seqParentIdArray = parentEventArray.flatMap((o) => o.eventTypeGroupSequenceIdentity);
                seqParentIdArray = seqParentIdArray.filter(function (v, i) {
                    return seqParentIdArray.indexOf(v) === i;
                });
                seqParentIdArray.forEach((seqParentIdItem) => {
                    // Find all items in parent sequence
                    const seqParentArray = parentEventArray.filter((seqItem) => seqItem.eventTypeGroupSequenceIdentity === seqParentIdItem);
                    if (seqParentArray && seqParentArray.length > 0) {
                        const ind = findMissingEventTypeInSequence(seqParentArray);
                        if (ind !== -1) {
                            // Finn all items in sequence dependent on found parent (e.g. Pumping)
                            const depEvArray = eventArray.filter(
                                (ev) => ev.eventTypeGroupSequenceDependencyIdentity === seqParentArray[0].eventTypeGroupSequenceIdentity
                            );
                            if (depEvArray && depEvArray.length > 0) {
                                const ind2 = findMissingEventTypeInSequence(depEvArray);
                                if (ind2 === -1 && isMultiple(depEvArray[0].eventType.eventTypeGroup, node.firstTimeInPortCall)) {
                                    const evType = createNewEventType(eventType);
                                    if (evType) {
                                        evType.eventTypeGroupSequenceIdentity = generateUuid();
                                        evType.eventTypeGroupSequenceDependencyIdentity = seqParentArray[0].eventTypeGroupSequenceIdentity;
                                        evType.parentSequenceNumber = seqParentArray[0].concurrentSequenceNumber;
                                        const seqCounter = Math.max(
                                            ...depEvArray.map(function (o) {
                                                return o.concurrentSequenceNumber;
                                            })
                                        );
                                        evType.displayNameToolbox = getEventTypeNameForToolbox(
                                            eventArray,
                                            depEvArray,
                                            null,
                                            evType,
                                            false,
                                            seqCounter,
                                            eventTypeData,
                                            node
                                        );
                                        eventTypeArray.push(evType);
                                    }
                                }
                            } else {
                                const evType = createNewEventType(eventType);
                                if (evType) {
                                    evType.eventTypeGroupSequenceIdentity = generateUuid();
                                    evType.eventTypeGroupSequenceDependencyIdentity = seqParentArray[0].eventTypeGroupSequenceIdentity;
                                    evType.parentSequenceNumber = seqParentArray[0].concurrentSequenceNumber;
                                    evType.displayNameToolbox = getEventTypeNameForToolbox(
                                        eventArray,
                                        depEvArray,
                                        null,
                                        evType,
                                        false,
                                        1,
                                        eventTypeData,
                                        node
                                    );
                                    eventTypeArray.push(evType);
                                }
                            }
                        }
                    }
                });
            }
        }
        return eventTypeArray;
    };

    // If event type has empty input list return true
    const eventTypeHasEmptyInputList = (eventType: EventType, selectedNode: IRotationNode): boolean => {
        // Handle null selectedNode gracefully
        if (!selectedNode) {
            return false;
        }

        let hasEmptyList = false;
        // Only add event type if input groups are not empty
        if (eventType.eventTypeInputArray && eventType.eventTypeInputArray.length > 0) {
            // Check if any of the event type inputs are of type dropdown
            const eventTypeWithDropdownArray = eventType.eventTypeInputArray.filter((etia) => {
                return etia.inputDataTypeId === InputDataTypeEnum.Dropdown;
            });
            // Check if any dropdowns for event type is of type cargo...
            const eventTypeWithCargoInput = eventTypeWithDropdownArray.find((etwda) => {
                return isCargoDropdown(etwda.dropdownGroup.dropdownTypeId);
            });
            if (eventTypeWithCargoInput) {
                if (selectedNode.type === RotationTreeEnum.PortCall) {
                    const portCall: PortCall | null = getPortCallFromNode(selectedNode);
                    if (portCall) {
                        // Find all cargo operations for all berths within port call
                        // const cargoOperationArray = _.flatten(_.pluck(portCall.berthCallArray, 'cargoOperationArray')) as CargoOperation[];
                        let cargoOperationArray = portCall.berthCallArray.flatMap((o) => o.cargoOperationArray);
                        cargoOperationArray = cargoOperationArray.filter(function (v, i) {
                            return cargoOperationArray.indexOf(v) === i;
                        });
                        if (cargoOperationArray && cargoOperationArray.length > 0) {
                            let itemOrder = 0;
                            // const evTypeArrayForPortCall: DropdownItem[] = _.chain(cargoOperationArray)
                            //   .filter(co => filterCargoOperation(eventTypeWithCargoInput.dropdownGroup.dropdownTypeId, co, false, false))
                            //   .map(cargoOperation => {
                            //     itemOrder++;
                            //     return dropdownItemFactory.newDropdownItemFromCode(cargoOperation.cargoOperationId, -1, cargoOperation.name, itemOrder, false);
                            //   })
                            //   .value();
                            const evTypeArrayForPortCall = cargoOperationArray
                                .filter((co) => filterCargoOperation(eventTypeWithCargoInput.dropdownGroup.dropdownTypeId, co, false, false))
                                .map((cargoOperation) => {
                                    itemOrder++;
                                    return dropdownItemFactory.newDropdownItemFromCode(
                                        cargoOperation.cargoOperationId,
                                        -1,
                                        cargoOperation.name,
                                        itemOrder,
                                        false
                                    );
                                });
                            // Check if cargo operations of correct type
                            if (!evTypeArrayForPortCall || evTypeArrayForPortCall.length === 0) {
                                hasEmptyList = true;
                            }
                        } else {
                            hasEmptyList = true;
                        }
                    }
                } else if (selectedNode.type === RotationTreeEnum.BerthCall) {
                    const berthCall: BerthCall | null = getBerthCallFromNode(selectedNode);
                    if (berthCall) {
                        const evTypeArray = berthCall.cargoOperationArray.filter((co) => {
                            return filterCargoOperation(eventTypeWithCargoInput.dropdownGroup.dropdownTypeId, co, false, false);
                        });
                        if (!(evTypeArray && evTypeArray.length > 0)) {
                            hasEmptyList = true;
                        }
                    }
                }
            }
        }
        return hasEmptyList;
    };

    // Check conditions before adding new event type to list
    const conditionalAddEventType = (eventTypeArray: EventType[], addEventType: EventType, selectedNode: IRotationNode): EventType[] => {
        // Check if event type has empty input list
        if (!eventTypeHasEmptyInputList(addEventType, selectedNode)) {
            eventTypeArray.push(addEventType);
        }
        return eventTypeArray;
    };

    const getEventTypeGroups = (
        eventTypeGrArray: EventTypeGroup[],
        eventArray: TankersEvent[],
        selectedNode: IRotationNode,
        eventTypeData: EventTypeData,
        sort: boolean,
        isRetroactiveCargoModeEnabled = false
    ): EventTypeGroup[] => {
        // Find all event type groups with notAfterEventTypeGroup not set, and requiredPreviousTypeId=null/value
        const withRequiredEventGroupArray: EventTypeGroup[] = getAllAvailableWithRequiredEventTypeGroups(eventTypeGrArray, eventArray, selectedNode);

        // Find all event type groups for context and empty/not finished/multiple
        let eventTypeGroupArray: EventTypeGroup[] = getAllAvailableEventTypeGroups(withRequiredEventGroupArray, eventArray, selectedNode);

        if (eventTypeData) {
            // Find all event type groups with sequence dependency (both notAfterEventTypeGroup = true and requiredPreviousTypeId = true)
            const seqDepEventTypeArray = getAllAvailableWithParentEventTypeGroups(eventTypeGrArray, eventArray, eventTypeData, selectedNode);

            eventTypeGroupArray = eventTypeGroupArray.concat(seqDepEventTypeArray);

            eventTypeGroupArray = getAllAvailableWithConditionaMandatoryEventTypeGroups(
                eventTypeGroupArray,
                eventArray,
                eventTypeData,
                isRetroactiveCargoModeEnabled,
                selectedNode
            );

            // Get unique event type groups
            let eventTypeGrArr: EventTypeGroup[] = [];
            eventTypeGroupArray.forEach((gr) => {
                if (!eventTypeGrArr.find((o) => o.eventTypeGroupId === gr.eventTypeGroupId)) {
                    eventTypeGrArr.push(gr);
                }
                // eventTypeGrArr.push(gr);
            });
            if (sort) {
                const sortedEventGroupArray = sortOrderedEventTypeGroups(eventTypeGrArr);
                eventTypeGrArr = getNextAvailableEventTypeGroup(sortedEventGroupArray, eventArray, selectedNode);
            }
            return eventTypeGrArr;
        }
        return eventTypeGroupArray;
    };

    const getEventTypes = (
        eventTypeGrArray: EventTypeGroup[],
        eventArray: TankersEvent[],
        selectedNode: IRotationNode,
        eventTypeData: EventTypeData,
        sort: boolean,
        isRetroactiveCargoModeEnabled = false
    ): EventType[] => {
        // Handle null selectedNode gracefully
        if (!selectedNode) {
            return [];
        }

        let availableEventTypeArray: EventType[] = [];

        const eventTypeGroupArray = getEventTypeGroups(
            eventTypeGrArray,
            eventArray,
            selectedNode,
            eventTypeData,
            sort,
            isRetroactiveCargoModeEnabled
        );

        if (eventTypeGroupArray && eventTypeGroupArray.length > 0) {
            eventTypeGroupArray.forEach((grItem) => {
                const evTypeList = getNextAvailableEventType(grItem, eventArray, selectedNode, eventTypeData, isRetroactiveCargoModeEnabled);
                if (evTypeList && evTypeList.length > 0) {
                    evTypeList.forEach((evTypeItem) => {
                        evTypeItem.levelId = selectedNode.type;
                        availableEventTypeArray = conditionalAddEventType(availableEventTypeArray, evTypeItem, selectedNode);
                    });
                }
            });
        }
        return availableEventTypeArray;
    };

    const getEventTypeGroupArray = (eventTypeData: EventTypeData, selectedNode: IRotationNode): EventTypeGroup[] => {
        // Handle null selectedNode gracefully
        if (!selectedNode) {
            return [];
        }

        if (eventTypeData && eventTypeData.eventTypeLevelArray && eventTypeData.eventTypeLevelArray.length > 0) {
            const eventTypeLevel = eventTypeData.eventTypeLevelArray.find((ev) => ev.eventTypeLevelId === selectedNode.type);
            if (eventTypeLevel && eventTypeLevel.eventTypeGroupArray.length > 0) {
                return filterEventTypeGroup(eventTypeLevel.eventTypeGroupArray, selectedNode);
            }
        }
        return [];
    };

    // Main method for populating mandatory and optional event types in toolbox
    const getAvailableEventTypes = (
        eventArray: TankersEvent[],
        eventTypeData: EventTypeData,
        node: IRotationNode,
        isRetroactiveCargoModeEnabled = false
    ): EventType[] => {
        // Handle null node gracefully
        if (!node) {
            return [];
        }

        let availableEventTypeArray: EventType[] = [];

        const eventTypeGroupArray: EventTypeGroup[] = getEventTypeGroupArray(eventTypeData, node);

        eventArray = getParentEvents(eventArray, node);

        eventArray = eventArray?.filter((ev) => !ev.deleted);

        if (eventTypeGroupArray && eventTypeGroupArray.length > 0) {
            availableEventTypeArray = getEventTypes(
                getMandatoryOrderedEventTypeGroups(eventTypeGroupArray, node),
                eventArray,
                node,
                eventTypeData,
                true,
                isRetroactiveCargoModeEnabled
            );

            availableEventTypeArray = availableEventTypeArray.concat(
                getEventTypes(
                    getMandatoryNotOrderedEventTypeGroups(eventArray, eventTypeGroupArray, eventTypeData.conditionalMandatoryArray, node),
                    eventArray,
                    node,
                    eventTypeData,
                    false,
                    isRetroactiveCargoModeEnabled
                )
            );

            availableEventTypeArray = availableEventTypeArray.concat(
                getEventTypes(
                    getOptionalEventTypeGroups(eventTypeGroupArray, node),
                    eventArray,
                    node,
                    eventTypeData,
                    false,
                    isRetroactiveCargoModeEnabled
                )
            );
        }
        return availableEventTypeArray.sort((a, b) => a.name.localeCompare(b.name));
    };

    // Checks if an event type group is mandatory in the rotation tree
    const isMandatoryInRotationTree = (evTypeGroup: EventTypeGroup, eventList: TankersEvent[], node: IRotationNode): boolean => {
        if (eventList && eventList.length > 0) {
            // Find events for group
            const eventListForGroup = eventList.filter((ev) => ev.eventType && ev.eventType.eventTypeGroupId === evTypeGroup.eventTypeGroupId);
            if (eventListForGroup && eventListForGroup.length > 0) {
                let mandatory = false;
                // const seqIdArray: string[] = _.uniq(_.pluck(eventListForGroup, 'eventTypeGroupSequenceIdentity'));
                let seqIdArray: string[] = eventListForGroup.flatMap((o) => o.eventTypeGroupSequenceIdentity);
                seqIdArray = seqIdArray.filter(function (v, i) {
                    return seqIdArray.indexOf(v) === i;
                });
                let keepGoing = true;
                let ind = 0;
                while (keepGoing) {
                    // Find all items in sequence
                    const seqArray = filterDuplicateEvents(
                        eventListForGroup.filter((seqItem) => seqItem.eventTypeGroupSequenceIdentity === seqIdArray[ind])
                    );
                    //const seqArray = eventListForGroup.filter(seqItem => seqItem.eventTypeGroupSequenceIdentity === seqIdArray[ind]);
                    if (seqArray && seqArray.length > 0) {
                        const seqInd = findMissingEventTypeInSequence(seqArray);
                        if (seqInd !== -1) {
                            keepGoing = false;
                            const sortedEvTypeList = sortOrderedEventTypes(evTypeGroup.eventTypeArray);
                            if (sortedEvTypeList.length > seqInd + 1) {
                                mandatory = sortedEvTypeList[seqInd + 1].mandatory;
                            } else {
                                mandatory = true;
                            }
                        } else {
                            ind++;
                            keepGoing = ind < seqIdArray.length;
                        }
                    } else {
                        ind++;
                        keepGoing = ind < seqIdArray.length;
                    }
                }
                return mandatory;
            } else {
                return isEtgMandatory(evTypeGroup, node) && isEnabledFrom(evTypeGroup, getFullEventArray(), node);
            }
        } else {
            return isEtgMandatory(evTypeGroup, node) && isEnabledFrom(evTypeGroup, getFullEventArray(), node);
        }
    };

    const isEnabledFrom = (evTypeGroup: EventTypeGroup | null, eventList: TankersEvent[], selectedNode: IRotationNode): boolean => {
        if (!evTypeGroup) {
            return false;
        }
        if (!evTypeGroup.enabledFrom) {
            return true;
        }
        const portCallId = getPortCallIdFromNode(selectedNode);
        if (portCallId) {
            const pcEventList: TankersEvent[] = eventList
                .filter((ev: TankersEvent) => ev.portCallId === portCallId)
                .sort((a, b) => compareActualEventDateTime(a, b, false));
            if (pcEventList && pcEventList.length > 0) {
                if (isDateAfter(pcEventList[pcEventList.length - 1].eventDate.actualDateTimeUtc, evTypeGroup.enabledFrom)) {
                    return true;
                }
            }
        }
        return false;
    };

    const isEtgMandatory = (evTypeGroup: EventTypeGroup | null, node: IRotationNode | null): boolean => {
        if (!evTypeGroup || !node) {
            return false;
        }
        return isMandatory(evTypeGroup, node.firstTimeInPortCall);
    };

    const isActualMandatory = (evType: EventType, eventArray: TankersEvent[], eventTypeData: EventTypeData, node: IRotationNode): boolean => {
        if (!evType.actualMandatory && !evType.parentSequenceNumber) return false;
        const evListInGroup: TankersEvent[] = eventArray.filter((event) => {
            return event.eventType && event.eventType.eventTypeGroupId === evType.eventTypeGroup.eventTypeGroupId;
        });
        if (evListInGroup && evListInGroup.length > 0) {
            // Event type list for event type group
            const indEv = findMissingEventTypeInSequence(evListInGroup);
            // All event seq finished and only 1 event in group
            if (indEv === -1 && evType.eventTypeGroup.eventTypeArray.length === 1) {
                return false;
            }
        } else {
            return evType.mandatory && isEtgMandatory(evType.eventTypeGroup, node);
        }

        let isActMand = isMandatoryInToolbox(evType, eventArray, node);
        if (!isActMand) {
            const grArray = eventTypeData.eventTypeLevelArray.flatMap((o) => o.eventTypeGroupArray);
            const etGrList = grArray.filter((et) => getRequiredPreviousTypeId(et, node.firstTimeInPortCall) === evType.eventTypeId);
            if (etGrList && etGrList.length > 0) {
                let keepGoing = true;
                let ind = 0;
                while (keepGoing) {
                    // Check if unfinished sequence or multiple
                    const eventListForGroup: TankersEvent[] = eventArray.filter((event) => {
                        return event.eventType && event.eventType.eventTypeGroupId === etGrList[ind].eventTypeGroupId;
                    });
                    if (!eventListForGroup || eventListForGroup.length === 0) {
                        isActMand = isEtgMandatory(etGrList[ind], node) && etGrList[ind].eventTypeArray[0].mandatory;
                        keepGoing = !isActMand;
                    } else {
                        const ind = findMissingEventTypeInSequence(eventListForGroup);
                        if (ind !== -1) {
                            isActMand = true;
                            keepGoing = false;
                        }
                    }
                    if (keepGoing) {
                        ind++;
                        keepGoing = ind < etGrList.length;
                    }
                }
            }
        }
        return isActMand;
    };

    const isSeqNotFinished = (eventArray: TankersEvent[], evType: EventType, seq: number | null): boolean => {
        const eventListForGroup = filterSameEventTypeGroupAndLevel(eventArray, evType);
        //const eventListForGroup = eventArray.filter(ev => ev.eventType.eventTypeGroupId === evType.eventTypeGroupId);
        if (eventListForGroup && eventListForGroup.length > 0) {
            let seqArray: TankersEvent[] = [];
            if (seq) {
                seqArray = eventListForGroup.filter((ev) => ev.concurrentSequenceNumber === seq);
            } else {
                seqArray = eventListForGroup;
            }
            if (seqArray && seqArray.length > 0) {
                const ind = findMissingEventTypeInSequence(seqArray);
                if (ind !== -1) {
                    return true;
                }
            }
        }
        return false;
    };

    const getMandatoryOptionalList = (
        items: EventType[],
        mandatoryFilter: boolean,
        contextObj: IRotationNode,
        fullEventList: TankersEvent[],
        eventTypeData: EventTypeData
    ): EventType[] => {
        const node = getRequiredContextObj(contextObj, contextObj.type);
        if (node) {
            const evTypeData = node.type === RotationTreeEnum.Sea ? eventTypeDataSea : eventTypeDataPort;
            const evList = filterEventsOnDeleted(fullEventList);
            const contextSpecificEventList = filterOnContext(node, evList);
            const evTypeArray: EventType[] = [];
            items.forEach((evType) => {
                const isMand = isActualMandatory(evType, contextSpecificEventList, evTypeData, node);
                const isEventTypeEnabled = isEnabledFrom(evType.eventTypeGroup, contextSpecificEventList, contextObj);
                if (isMand === mandatoryFilter && isEventTypeEnabled && contextObj.type === evType.levelId) {
                    const newEvType = createNewEventType(evType);
                    if (mandatoryFilter) newEvType.actualMandatory = isMand;
                    evTypeArray.push(newEvType);
                } else if (isMand && !mandatoryFilter && !getNotAfterEventTypeId(evType.eventTypeGroup, node.firstTimeInPortCall)) {
                    // Find parent
                    const parentEvType = getEventType(getRequiredPreviousTypeId(evType.eventTypeGroup, node.firstTimeInPortCall), null);
                    if (parentEvType) {
                        const eventListForParentGroup = fullEventList.filter((ev) => ev.eventType.eventTypeGroupId === parentEvType.eventTypeGroupId);
                        if (eventListForParentGroup && eventListForParentGroup.length > 0) {
                            if (hasConcurrentParentInCompleteTree(evType, eventTypeData, node)) {
                                // If parent is concurrent
                                let seqNrArray: number[] = eventListForParentGroup.flatMap((o) => o.concurrentSequenceNumber);
                                seqNrArray = seqNrArray.filter(function (v, i) {
                                    return seqNrArray.indexOf(v) === i;
                                });
                                seqNrArray = seqNrArray.filter((o) => o !== evType.parentSequenceNumber);
                                let keepGoing = true;
                                let seqInd = 0;
                                while (keepGoing) {
                                    const evArrayForSeq = fullEventList.filter((ev) => ev.concurrentSequenceNumber === seqNrArray[seqInd]);
                                    if (!isSeqNotFinished(evArrayForSeq, parentEvType, seqNrArray[seqInd])) {
                                        const sortedParentEvTypeList = sortOrderedEventTypes(parentEvType.eventTypeGroup.eventTypeArray);
                                        if (
                                            sortedParentEvTypeList &&
                                            sortedParentEvTypeList.length > 0 &&
                                            sortedParentEvTypeList[sortedParentEvTypeList.length - 1].eventTypeId ===
                                                evType.eventTypeGroup.requiredPreviousTypeId
                                        ) {
                                            const sortedEvTypeList = sortOrderedEventTypes(evType.eventTypeGroup.eventTypeArray);
                                            const newEvType = createNewEventType(sortedEvTypeList[0]);
                                            newEvType.parentSequenceNumber = null;
                                            evTypeArray.push(newEvType);
                                            keepGoing = false;
                                        }
                                    }
                                    seqInd++;
                                    if (seqInd === seqNrArray.length - 1) keepGoing = false;
                                }
                            } else {
                                // If parent is not concurrent
                                const ind = findMissingEventTypeInSequence(eventListForParentGroup);
                                if (ind !== -1 || !getNotAfterEventTypeId(evType.eventTypeGroup, node.firstTimeInPortCall)) {
                                    // parentListList.push(new ProcessItemList(getParentEventTypeList(eventArray, evType, null, evTypeArray, node)));
                                }
                            }
                        }
                    }
                }
            });
            return evTypeArray.sort((a, b) => a.name.localeCompare(b.name));
        }
        return [];
    };

    // Checks if an event type should be placed in the mandatory toolbox
    const isMandatoryInToolbox = (evType: EventType, eventList: TankersEvent[], node: IRotationNode): boolean => {
        // Sort event types in group
        const sortedEvType = sortOrderedEventTypes(evType.eventTypeGroup.eventTypeArray);
        if (sortedEvType && sortedEvType.length > 0) {
            if (eventList && eventList.length > 0) {
                // Find events in event type sequence
                const eventSeqArray = eventList.filter((seqItem) => seqItem.eventTypeGroupSequenceIdentity === evType.eventTypeGroupSequenceIdentity);
                if (eventSeqArray && eventSeqArray.length > 0) {
                    let ind = findMissingEventTypeInSequence(eventSeqArray);
                    if (ind === -1) {
                        ind = eventSeqArray.length - 1;
                    }
                    if (sortedEvType[sortedEvType.length - 1].eventTypeId === eventSeqArray[ind].eventTypeId) {
                        // All event types in event type group are created
                        if (isMultiple(evType.eventTypeGroup, node.firstTimeInPortCall)) {
                            // If multiple and mandatory, and event group already created, show event type in Optional
                            return false;
                        } else {
                            return true;
                        }
                    } else {
                        // Check if the EventType is Mandatory
                        return evType.mandatory;
                    }
                } else {
                    const evExist = eventList.find(
                        (ev) =>
                            ev.eventType &&
                            ev.eventType.eventTypeGroupId === evType.eventTypeGroupId &&
                            (!evType.parentSequenceNumber ||
                                (evType.eventTypeGroupSequenceIdentity === ev.eventTypeGroupSequenceIdentity &&
                                    ev.concurrentSequenceNumber === evType.parentSequenceNumber))
                    );
                    if (isMultiple(evType.eventTypeGroup, node.firstTimeInPortCall) && evExist) {
                        // If multiple, and event type already registered, show event type in Optional
                        return false;
                    } else {
                        return true;
                    }
                }
            } else {
                return true;
            }
        } else {
            return true;
        }
    };

    // Gets available event types for selected node
    const containMandatoryEvents = (
        eventArray: TankersEvent[],
        filteredEventArray: TankersEvent[],
        eventTypeData: EventTypeData,
        selectedNode: IRotationNode
    ): boolean => {
        let containMandatoryEvents = false;
        const availableEventTypeArray = getAvailableEventTypesMandatory(eventArray, eventTypeData, selectedNode);
        let eventTypeGroupIdArray: number[] = availableEventTypeArray.flatMap((o) => o.eventTypeGroupId);
        eventTypeGroupIdArray = eventTypeGroupIdArray.filter(function (v, i) {
            return eventTypeGroupIdArray.indexOf(v) === i;
        });
        const evTypeGrArray: EventTypeGroup[] = [];
        eventTypeGroupIdArray.forEach((id) => {
            const evTypeItem = availableEventTypeArray.find((item) => item.eventTypeGroupId === id);
            if (evTypeItem && evTypeItem.eventTypeGroup) {
                evTypeGrArray.push(evTypeItem.eventTypeGroup);
            }
        });
        if (evTypeGrArray.length > 0) {
            const mandatoryEventType = evTypeGrArray.find((etg) => {
                return isMandatoryInRotationTree(etg, filteredEventArray, selectedNode);
            });
            if (mandatoryEventType) {
                containMandatoryEvents = true;
            }
        }
        return containMandatoryEvents;
    };

    const isNodeInvalid = (eventArray: TankersEvent[], selectedNode: IRotationNode): boolean => {
        if (selectedNode.isMoved || selectedNode.isDeleted) {
            return true;
        }
        // const nodeEvArray = filterEventsOnDeleted(filterEventsOnActual(filterOnContext(selectedNode, eventArray)));
        // if (nodeEvArray && nodeEvArray.length > 0) {
        //     const inValidEv: TankersEvent | null =
        //         nodeEvArray.find((ev) => {
        //             const ddReg = ev.eventTypeInputRegList.find(
        //                 (inp) => !inp.isEmpty && inp.inputDataTypeId! === InputDataTypeEnum.Dropdown && inp.dropdownGroupReg
        //             );
        //             if (ddReg) {
        //                 if (ddReg.dropdownGroupReg.dropdownItemRegList.find((ddItem) => !ddItem.isValid)) {
        //                     return true;
        //                 }
        //             }
        //             return false;
        //         }) ?? null;
        //     return false;
        //     if (inValidEv) {
        //         return true;
        //     }
        // }
        return false;
    };

    // Check if rotation node contains remaining mandatory events, including dependent events
    const isNodeNotCompleted = (
        eventArray: TankersEvent[],
        filteredEventArray: TankersEvent[],
        eventTypeData: EventTypeData,
        selectedNode: IRotationNode
    ): boolean => {
        let isNotCompleted = false;

        // For node:
        //      -- If any mandatory event types on node, set hasMandatoryEvents = true
        //      -- Find all available event type groups, for each:
        //          -- Check if dependent, mandatory, not finished, event type groups (only the next level sufficient to check)

        if (eventArray && eventArray.length > 0) {
            if (!containMandatoryEvents(eventArray, filteredEventArray, eventTypeData, selectedNode)) {
                switch (selectedNode.type) {
                    case RotationTreeEnum.Sea:
                    case RotationTreeEnum.PortCall: {
                        const portNode = getRequiredContextObj(selectedNode, selectedNode.type) as PortCall;
                        if (portNode && portNode.berthCallArray && portNode.berthCallArray.length > 0) {
                            let keepGoingBc = true;
                            let indBc = 0;
                            while (keepGoingBc) {
                                const berthEvArray = filterEventsOnActual(filterOnContext(portNode.berthCallArray[indBc], eventArray));
                                if (!containMandatoryEvents(eventArray, berthEvArray, eventTypeData, portNode.berthCallArray[indBc])) {
                                    if (
                                        portNode.berthCallArray[indBc].cargoOperationArray &&
                                        portNode.berthCallArray[indBc].cargoOperationArray.length > 0
                                    ) {
                                        let keepGoingCo = true;
                                        let indCo = 0;
                                        while (keepGoingCo) {
                                            const cargoEvArray = filterEventsOnActual(
                                                filterOnContext(portNode.berthCallArray[indBc].cargoOperationArray[indCo], eventArray)
                                            );
                                            if (
                                                containMandatoryEvents(
                                                    eventArray,
                                                    cargoEvArray,
                                                    eventTypeData,
                                                    portNode.berthCallArray[indBc].cargoOperationArray[indCo]
                                                ) ||
                                                isNodeInvalid(eventArray, portNode.berthCallArray[indBc].cargoOperationArray[indCo])
                                            ) {
                                                keepGoingCo = false;
                                                isNotCompleted = true;
                                            } else {
                                                indCo++;
                                                keepGoingCo = indCo < portNode.berthCallArray[indBc].cargoOperationArray.length;
                                            }
                                        }
                                        if (isNotCompleted) {
                                            keepGoingBc = false;
                                        } else {
                                            indBc++;
                                            keepGoingBc = indBc < portNode.berthCallArray.length;
                                        }
                                    } else {
                                        indBc++;
                                        keepGoingBc = indBc < portNode.berthCallArray.length;
                                    }
                                } else {
                                    keepGoingBc = false;
                                    isNotCompleted = true;
                                }
                            }
                        }
                        break;
                    }
                    case RotationTreeEnum.BerthCall: {
                        const berthCall = getRequiredContextObj(selectedNode, selectedNode.type) as BerthCall;
                        if (berthCall && berthCall.cargoOperationArray && berthCall.cargoOperationArray.length > 0) {
                            let keepGoing = true;
                            let ind = 0;
                            while (keepGoing) {
                                const cargoEvArray = filterEventsOnActual(filterOnContext(berthCall.cargoOperationArray[ind], eventArray));
                                if (
                                    containMandatoryEvents(eventArray, cargoEvArray, eventTypeData, berthCall.cargoOperationArray[ind]) ||
                                    isNodeInvalid(eventArray, berthCall.cargoOperationArray[ind])
                                ) {
                                    keepGoing = false;
                                    isNotCompleted = true;
                                } else {
                                    ind++;
                                    keepGoing = ind < berthCall.cargoOperationArray.length;
                                }
                            }
                        }
                        break;
                    }
                    case RotationTreeEnum.CargoOperation:
                        isNotCompleted = isNodeInvalid(eventArray, selectedNode);
                        break;
                }
            } else {
                isNotCompleted = true;
            }
        }
        return isNotCompleted;
    };

    // Method to check if mandatory event types are not registered
    // Method to check if mandatory event types are not registered
    const getAvailableEventTypesMandatory = (eventArray: TankersEvent[], eventTypeData: EventTypeData, node: IRotationNode): EventType[] => {
        let availableEventTypeArray: EventType[] = [];

        if (eventTypeData && eventTypeData.eventTypeLevelArray && eventTypeData.eventTypeLevelArray.length > 0) {
            eventArray = getParentEvents(eventArray, node);
            eventArray = eventArray.filter((ev) => !ev.deleted);

            const eventTypeLevel = eventTypeData.eventTypeLevelArray.find((ev) => ev.eventTypeLevelId === node.type);
            if (eventTypeLevel && eventTypeLevel.eventTypeGroupArray.length > 0) {
                const eventTypeGroupArray = filterEventTypeGroup(eventTypeLevel.eventTypeGroupArray, node);
                availableEventTypeArray = getEventTypes(
                    getMandatoryOrderedEventTypeGroups(eventTypeGroupArray, node),
                    eventArray,
                    node,
                    eventTypeData,
                    true
                );

                availableEventTypeArray = availableEventTypeArray.concat(
                    getEventTypes(
                        getMandatoryNotOrderedEventTypeGroups(eventArray, eventTypeGroupArray, eventTypeData.conditionalMandatoryArray, node),
                        eventArray,
                        node,
                        eventTypeData,
                        false
                    )
                );

                availableEventTypeArray = availableEventTypeArray.concat(
                    getEventTypes(getOptionalEventTypeGroups(eventTypeGroupArray, node), eventArray, node, eventTypeData, false)
                );
            }
        }
        return availableEventTypeArray.sort((a, b) => a.name.localeCompare(b.name));
    };

    const canMoveEventsForMovedCargoOp = (
        rot: Rotation,
        eventArray: TankersEvent[],
        eventTypeData: EventTypeData,
        moveToBerthCall: BerthCall,
        moveFromCargoOperation: CargoOperation,
        node: IRotationNode
    ): MoveActionMsg => {
        const actionMsg = new MoveActionMsg(false, null, true, null, null, null);

        eventArray = eventArray.filter((ev) => !ev.deleted);

        // Find events for berth call moving to, including parent
        const bcToEventList = filterOnContextSingleLevelIncludeParents(moveToBerthCall, eventArray);

        // Find events for cargo operation moving from
        const coFromEventList = filterOnContextSingleLevel(moveFromCargoOperation, eventArray);
        if (!coFromEventList || coFromEventList.length === 0) {
            actionMsg.doAction = false;
            actionMsg.message = 'No events to move';
            return actionMsg;
        }

        // Find events for cargo operation moving to
        const movedToCo = getCargoOpRotationNode(
            rot,
            moveToBerthCall.portCall.portCallId,
            moveToBerthCall.berthCallId,
            moveFromCargoOperation.cargoOperationId
        );
        if (movedToCo) {
            const coToEventList = filterOnContextSingleLevel(movedToCo, eventArray);
            if (coToEventList && coToEventList.length > 0) {
                actionMsg.displayMoveIcon = true;
                actionMsg.id = movedToCo.id;
                actionMsg.doAction = false;
                actionMsg.message =
                    'Events are already registered for the cargo operation ' +
                    "'" +
                    movedToCo.tradeName +
                    "'" +
                    ' in berth call ' +
                    "'" +
                    moveToBerthCall.name +
                    "'";
                return actionMsg;
            }
        }

        // Split cargo operation events into event type group ids
        let etcIdList: number[] = [];
        coFromEventList.forEach((ev) => {
            etcIdList.push(ev.eventType.eventTypeGroupId);
        });
        // etcIdList = _.uniq(etcIdList);
        etcIdList = etcIdList.filter(function (v, i) {
            return etcIdList.indexOf(v) === i;
        });

        // For each event type group, check that the required conditions are met:
        //    - check required previous event type group
        //    - check required conditional mandatory event type groups

        etcIdList.forEach((etgId) => {
            // Find event type group for cargo operation events
            const etg = getEventTypeGroup(eventTypeData, etgId, null);

            // Check if requiredPreviousTypeId is set
            if (etg && getRequiredPreviousTypeId(etg, node.firstTimeInPortCall)) {
                const etReq = getEventType(getRequiredPreviousTypeId(etg, node.firstTimeInPortCall), null);
                if (etReq) {
                    const etgReq = getEventTypeGroup(eventTypeData, etReq.eventTypeGroupId, null);
                    if (etgReq && etgReq.levelId < RotationTreeEnum.CargoOperation) {
                        if (!bcToEventList.find((ev) => ev.eventTypeId === getRequiredPreviousTypeId(etg, node.firstTimeInPortCall))) {
                            actionMsg.doAction = false;
                            actionMsg.displayMoveIcon = true;
                            actionMsg.id = moveToBerthCall.id;
                            actionMsg.message = 'Required event type ' + etgReq.name + " missing in berth call '" + moveToBerthCall.name + "'";
                            return actionMsg;
                        }
                    }
                }
            } else if (etg && hasConditionalMandatory(etg)) {
                const actionOk = isConditionalMandatoryAction(etg, bcToEventList, coFromEventList, eventTypeData, moveToBerthCall);
                if (!actionOk.doAction) {
                    actionMsg.doAction = false;
                    actionMsg.displayMoveIcon = true;
                    actionMsg.id = moveToBerthCall.id;
                    actionMsg.message = actionOk.message;
                    return actionMsg;
                }
            }
        });
        return actionMsg;
    };

    const updateNodeStatus = (rot: Rotation, eventTypeData: EventTypeData) => {
        rot.portCallArray.forEach((pcItem) => {
            const firstTimeInPortCall = getFirstPortCallTime(pcItem, fullEventArray);
            pcItem.timezoneWarningText = seaTimezoneStatusText(rot, pcItem);
            pcItem.delayInputWarningText = delayInputExceededWarningText(rot, pcItem);
            pcItem.firstTimeInPortCall = firstTimeInPortCall;
            pcItem.nodeStatus = getNodeStatus(rot, pcItem, eventTypeData);
            pcItem.berthCallArray.forEach((bcItem) => {
                bcItem.delayInputWarningText = delayInputExceededWarningText(rot, bcItem);
                bcItem.firstTimeInPortCall = firstTimeInPortCall;
                bcItem.nodeStatus = getNodeStatus(rot, bcItem, eventTypeData);
                bcItem.cargoOperationArray.forEach((coItem) => {
                    coItem.delayInputWarningText = delayInputExceededWarningText(rot, coItem);
                    coItem.firstTimeInPortCall = firstTimeInPortCall;
                    coItem.nodeStatus = getNodeStatus(rot, coItem, eventTypeData);
                });
            });
        });
    };

    const findLatestPortCallWithEvents = (rot: Rotation): PortCall | null => {
        if (fullEventArray && fullEventArray.length > 0) {
            let ind = rot.portCallArray.length - 1;
            let portCall: PortCall | null = null;
            let keepGoing = true;
            while (keepGoing) {
                const evList = fullEventArray.find(
                    (ev) =>
                        !ev.deleted &&
                        ev.portCallId === rot.portCallArray[ind].portCallId &&
                        ev.eventTypeContextId === rot.portCallArray[ind].reportLocation
                );
                if (evList) {
                    keepGoing = false;
                    portCall = rot.portCallArray[ind];
                } else {
                    ind--;
                    if (ind < 0) keepGoing = false;
                }
            }
            return portCall;
        }
        return null;
    };

    // Gets mandatory event types for node
    // Finds latest port call with events
    // Finds index of latest port call with events in the port call list
    // Finds index of current port call
    // Returns true if:
    //      - There are mandatory event types for node
    //      - The latest port call with events is not the last in the port call list
    //      -
    const isMandatoryAndEmptyNode = (rot: Rotation, selectedNode: IRotationNode, eventTypeData: EventTypeData): boolean => {
        const portCall = getPortCallFromNode(selectedNode);
        if (portCall) {
            const evTypeData = selectedNode.type === RotationTreeEnum.Sea ? eventTypeDataSea : eventTypeDataPort;
            const evTypeList = getAvailableEventTypes(fullEventArray, evTypeData, selectedNode);

            const contextSpecificEventList = filterOnContext(selectedNode, fullEventArray);
            const mandatoryList = getMandatoryOptionalList(evTypeList, true, selectedNode, contextSpecificEventList, eventTypeData);
            if (mandatoryList && mandatoryList.length > 0) {
                const latestPortCall = findLatestPortCallWithEvents(rot);
                if (latestPortCall) {
                    const indLatestEvent = rot.portCallArray.findIndex(
                        (pc: PortCall) => pc.portCallId === latestPortCall.portCallId && pc.reportLocation === latestPortCall.reportLocation
                    );
                    if (indLatestEvent !== -1 && indLatestEvent < rot.portCallArray.length - 1) {
                        const indCurrentPortCall = rot.portCallArray.findIndex(
                            (pc: PortCall) => pc.portCallId === portCall.portCallId && pc.reportLocation === portCall.reportLocation
                        );
                        if (indCurrentPortCall <= indLatestEvent + 1) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    };

    const isStatusEmpty = (node: IRotationNode, eventTypeData: EventTypeData): boolean => {
        const evTypeData = node.type === RotationTreeEnum.Sea ? eventTypeDataSea : eventTypeDataPort;

        const evArray = filterOnContextSingleLevel(node, fullEventArray);
        if (evArray && evArray.length > 0) {
            // If mandatory events for optional events: status not empty
            const evTypeList = getAvailableEventTypes(fullEventArray, evTypeData, node);
            const contextSpecificEventList = filterOnContext(node, fullEventArray);
            const mandatoryList = getMandatoryOptionalList(evTypeList, true, node, contextSpecificEventList, eventTypeData);
            if (mandatoryList && mandatoryList.length > 0) {
                return false;
            }

            // If one or more mandatory events registered: status not empty
            const evTypeGrMand = evArray.find((o) => {
                const evTypeGr = getEventTypeGroup(evTypeData, o.eventType.eventTypeGroupId, null);
                if (evTypeGr) {
                    return isEtgMandatory(evTypeGr, node);
                }
                return false;
            });
            if (evTypeGrMand) {
                return false;
            }
        }
        return true;
    };

    const getNodeStatus = (rot: Rotation, selectedNode: IRotationNode, eventTypeData: EventTypeData): string => {
        if (isMandatoryAndEmptyNode(rot, selectedNode, eventTypeData) || !isStatusEmpty(selectedNode, eventTypeData)) {
            const filteredEventArray = filterEventsOnActual(filterOnContextSeaIncludePort(selectedNode, fullEventArray));
            if (
                isNodeNotCompleted(
                    fullEventArray,
                    filteredEventArray,
                    selectedNode.type === RotationTreeEnum.Sea ? eventTypeDataSea : eventTypeDataPort,
                    selectedNode
                )
            ) {
                return 'edited';
            } else {
                return 'completed';
            }
        }
        return '';
    };

    // DropdownItemId 105: -2h
    // DropdownItemId 106: -1.5h
    // DropdownItemId 107: -1h
    // DropdownItemId 108: -0.5h
    // DropdownItemId 109: +0.5h
    // DropdownItemId 110: +1h
    // DropdownItemId 111: +1.5h
    // DropdownItemId 112: +2h
    const adjustHoursSum = (portCallId: number): number => {
        let adHoursSum = 0;
        const evAdjustHoursList = fullEventArray.filter((o) => !o.deleted && o.portCallId === portCallId && o.eventTypeId === ADJUST_HOURS);
        if (evAdjustHoursList && evAdjustHoursList.length > 0) {
            //adHoursSum = 0;
            evAdjustHoursList.forEach((ev) => {
                if (ev.eventTypeInputRegList && ev.eventTypeInputRegList.length > 0) {
                    const adHoursDrDown = ev.eventTypeInputRegList.find((o) => o.eventTypeInputId === ADJUST_HOURS_DD);
                    if (
                        adHoursDrDown &&
                        adHoursDrDown.dropdownGroupReg &&
                        adHoursDrDown.dropdownGroupReg.dropdownItemRegList &&
                        adHoursDrDown.dropdownGroupReg.dropdownItemRegList.length === 1
                    ) {
                        switch (adHoursDrDown.dropdownGroupReg.dropdownItemRegList[0].dropdownItemId) {
                            case ADJUST_HOURS_MINUS_2H:
                                adHoursSum -= 2;
                                break;
                            case ADJUST_HOURS_MINUS_15H:
                                adHoursSum -= 1.5;
                                break;
                            case ADJUST_HOURS_MINUS_1H:
                                adHoursSum -= 1;
                                break;
                            case ADJUST_HOURS_MINUS_05H:
                                adHoursSum -= 0.5;
                                break;
                            case ADJUST_HOURS_PLUSS_2H:
                                adHoursSum += 0.5;
                                break;
                            case ADJUST_HOURS_PLUSS_15H:
                                adHoursSum += 1;
                                break;
                            case ADJUST_HOURS_PLUSS_1H:
                                adHoursSum += 1.5;
                                break;
                            case ADJUST_HOURS_PLUSS_05H:
                                adHoursSum += 2;
                                break;
                        }
                    }
                }
            });
        }
        return adHoursSum;
    };

    const adjustHoursSumForIDL = (portCallId: number): number => {
        let adHoursSum = 0;
        const evIDLCrossingList = fullEventArray.filter((o) => !o.deleted && o.portCallId === portCallId && o.eventTypeId === IDL_CROSSING);
        if (evIDLCrossingList && evIDLCrossingList.length > 0) {
            evIDLCrossingList.forEach((ev) => {
                if (ev.eventTypeInputRegList && ev.eventTypeInputRegList.length > 0) {
                    const hoursDrDown = ev.eventTypeInputRegList.find((o) => o.eventTypeInputId === IDL_CROSSING_DD);
                    if (
                        hoursDrDown &&
                        hoursDrDown.dropdownGroupReg &&
                        hoursDrDown.dropdownGroupReg.dropdownItemRegList &&
                        hoursDrDown.dropdownGroupReg.dropdownItemRegList.length === 1
                    ) {
                        switch (hoursDrDown.dropdownGroupReg.dropdownItemRegList[0].dropdownItemId) {
                            case IDL_CROSSING_PLUSS_24H:
                                adHoursSum += 24;
                                break;
                            case IDL_CROSSING_MINUS_24H:
                                adHoursSum -= 24;
                                break;
                        }
                    }
                }
            });
        }
        return adHoursSum;
    };

    const numbersOdIDLCrossings = (portCallId: number): number => {
        const evIDLCrossingList = fullEventArray.filter((o) => !o.deleted && o.portCallId === portCallId && o.eventTypeId === IDL_CROSSING);
        return evIDLCrossingList.length;
    };

    const timesStr = (nr: number): string => {
        return nr > 1 ? ' times' : ' time';
    };

    const seaTimezoneStatusText = (rot: Rotation, presentPortCall: PortCall): string | null => {
        if (presentPortCall.reportLocation === ReportLocationEnum.Port) {
            const evPresentEosp = fullEventArray.find((o) => !o.deleted && o.portCallId === presentPortCall.portCallId && o.eventTypeId === EOSP);
            if (evPresentEosp && evPresentEosp.frontendVersion >= FrontendVersionEnum.OrcaWeb) {
                const pcInd = rot.portCallArray.findIndex((o) => o.portCallId === presentPortCall.portCallId);
                if (pcInd > 1) {
                    const prevPortCall = rot.portCallArray[pcInd - 2];
                    const evPreviousSosp = fullEventArray.find(
                        (o) => !o.deleted && o.portCallId === prevPortCall.portCallId && o.eventTypeId === SOSP
                    );
                    if (evPreviousSosp) {
                        // Get utcOffset for this present call
                        const presentPortCallOffset = getImosOffset(presentPortCall.port, evPresentEosp.eventDate.actualDateTimeLocal);
                        // Get timezone for previous port call and current date (in case of change to DST)
                        const previousPortCallOffset = getImosOffset(prevPortCall.port, evPresentEosp.eventDate.actualDateTimeLocal);
                        let portDiff = previousPortCallOffset - presentPortCallOffset;
                        // var portDiff = Math.abs(presentPortCallOffset - previousPortCallOffset);
                        const prevSea = rot.portCallArray[pcInd - 1];
                        const adjustHoursDiff = adjustHoursSum(prevSea.portCallId);
                        const adjustHoursForIDLCrossing = adjustHoursSumForIDL(prevSea.portCallId);
                        portDiff -= adjustHoursForIDLCrossing;
                        if (adjustHoursDiff !== portDiff) {
                            const nrOfIDLCrossings = numbersOdIDLCrossings(prevSea.portCallId);
                            const IDLCrossingStr =
                                nrOfIDLCrossings > 0 ? ' (crossing IDL)' + nrOfIDLCrossings.toString() + timesStr(nrOfIDLCrossings) : '';
                            const str =
                                'Time difference between ' +
                                getPortCallName(prevPortCall) +
                                ' and ' +
                                getPortCallName(presentPortCall) +
                                ' is ' +
                                String(portDiff) +
                                'h' +
                                IDLCrossingStr +
                                '. This is different from registered adjusted timezone hours: ' +
                                String(adjustHoursDiff) +
                                'h. Please adjust time zone change events and/or IDL event(s)';
                            return str;
                        }
                    }
                }
            }
        }
        return null;
    };

    const delayInputExceededWarningText = (rot: Rotation, node: IRotationNode): string | null => {
        let str: string | null = null;
        const portCall = getPortCallFromNode(node);
        const portCallEvList = filterOnContext(portCall, fullEventArray);
        let nodeStr = '';
        switch (node.type) {
            case RotationTreeEnum.Sea:
                nodeStr = 'sea leg';
                break;
            case RotationTreeEnum.PortCall:
                nodeStr = 'port call';
                break;
            case RotationTreeEnum.BerthCall:
                nodeStr = 'berth call';
                break;
            case RotationTreeEnum.CargoOperation:
                nodeStr = 'cargo operation';
                break;
        }
        if (isFeatureEnabled(Feature.DelayReason, portCallEvList)) {
            let andStr = '';
            let theStr = 'The ';
            const nodeEventArray = filterOnContext(node, fullEventArray);
            const hasDelayExceededInpAndMissingInput = nodeEventArray.find((ev) => {
                const evNode = getRotationNodeFromEvent(rot, ev);
                const contextIncludeParentSpecificEventArray = filterOnContextIncludeParentWithBerthCall(evNode, fullEventArray);
                return eventHasDelayAndMissingInput(rot, ev, contextIncludeParentSpecificEventArray, null);
            });
            const hasNoDelayAndExistingInputInp = nodeEventArray.find((ev) => {
                const evNode = getRotationNodeFromEvent(rot, ev);
                const contextIncludeParentSpecificEventArray = filterOnContextIncludeParentWithBerthCall(evNode, fullEventArray);
                return eventHasNoDelayAndExistingInput(rot, ev, contextIncludeParentSpecificEventArray, null);
            });
            if (Boolean(hasDelayExceededInpAndMissingInput) && Boolean(hasNoDelayAndExistingInputInp)) {
                andStr = ', and ';
            }

            if (hasDelayExceededInpAndMissingInput) {
                str = 'The ' + nodeStr + ' has events with delay exceeding the limit and missing delay input (please add input)';
            }
            if (hasNoDelayAndExistingInputInp) {
                if (hasDelayExceededInpAndMissingInput) {
                    theStr = 'the ';
                } else {
                    str = '';
                }
                str = str + andStr + theStr + nodeStr + ' has events with no delay and existing delay input (please remove by saving)';
            }
        } else {
            const nodeEventArray = filterOnContext(node, fullEventArray);
            const hasDelayAndExistingInput = nodeEventArray.find((ev) => {
                return eventHasDelayAndExistingInput(ev);
            });
            if (hasDelayAndExistingInput) {
                str = 'The ' + nodeStr + ' has events with delay input (please remove by saving)';
            }
        }
        return str;
    };

    return {
        getAvailableEventTypes,
        getMandatoryOptionalList,
        createEventName,
        filterOnContext,
        isNodeNotCompleted,
        canMoveEventsForMovedCargoOp,
        updateNodeStatus,
    };
};
